import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import axios from 'axios';
import {
    Dropdown, Drawer, Tree, Button, Spin, Radio, Form, Progress,
    Descriptions, Modal, Popconfirm, Layout, Alert, Upload, message, Space,
    Input, Table, Row, Col, Badge, Tooltip, Select, Checkbox,
} from 'antd';
import {
    MenuOutlined, CloudUploadOutlined, FileAddOutlined, FileZipOutlined,
    InboxOutlined, SearchOutlined, FolderAddFilled, EyeOutlined,
    FileExclamationOutlined, CloseOutlined, FileOutlined, EditOutlined,
    SaveOutlined, FolderOutlined, DownloadOutlined, DeleteOutlined,
    WarningOutlined, QuestionCircleOutlined, ReloadOutlined, BarChartOutlined,
    FileSyncOutlined,
} from '@ant-design/icons';
import config from '../../config';
import { selectAppPermissions } from '../../store/auth/selectors';
import { checkFolderName, createFolder } from '../../services/project/checkFolder';
import { logAction } from './logActionsPOA';
import { ActualizarTipoEntrega, ValidarEstadoActa } from '../../services/poa/get_poaDocumentosMedios';
import { enviarHistorico } from '../../services/poa/check_file_service';
import { ModalSoporteReemplazo } from '../DocumentoReemplazo/ModalSoporteReemplazo';
import FileManagerModal from '../PoaEliminar/FileManagerModal';
import Informe332 from '../informe332/Informe332';
import { useDownloadManager } from '../../context/DownloadManagerContext';
import './FileManagerPOA.css';

const { Content, Sider } = Layout;
const { Dragger } = Upload;
const { DirectoryTree } = Tree;
const baseUrl = config.baseUrl;

// ─────────────────────────────────────────────────────────────────────────────
// UTILIDADES PURAS (fuera del componente para evitar recreaciones)
// ─────────────────────────────────────────────────────────────────────────────

/** Normaliza texto: quita tildes y pasa a minúsculas */
const normalize = (str) =>
    str?.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim() ?? '';

/** Limpia el prefijo numérico de un nombre de archivo */
const cleanFileName = (encodedFileName) => {
    const decoded = decodeURIComponent(encodedFileName);
    const match = decoded.match(/^(\d+)-(.+)$/);
    return match ? match[2] : decoded;
};

/** Trunca texto con elipsis */
const truncateText = (text, maxLength) => {
    if (!text || typeof text !== 'string') return '';
    return text.length <= maxLength ? text : `${text.substring(0, maxLength)}...`;
};

/** Limpia prefijo numérico y luego trunca */
const processFileName = (fileName, maxLength) =>
    truncateText(cleanFileName(fileName), maxLength);

/**
 * Indica si el navegador puede mostrar este tipo de archivo directamente en
 * una pestaña (PDF, imágenes, texto, audio/video). Para el resto (xlsx, docx,
 * zip, etc.) no tiene sentido "abrirlo": hay que forzar la descarga.
 */
/**
 * Indica si el navegador puede mostrar este tipo de archivo directamente en
 * una pestaña (PDF, imágenes, texto, audio/video) a partir de su extensión,
 * sin necesidad de descargarlo primero para leer su Content-Type. Para el
 * resto (xlsx, docx, zip, etc.) no tiene sentido "abrirlo": hay que forzar
 * la descarga.
 */
const RENDERABLE_EXTENSIONS = new Set([
    'pdf',
    'jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'ico', 'tif', 'tiff',
    'txt', 'csv', 'json', 'xml', 'log', 'md',
    'mp4', 'webm', 'ogv', 'mov',
    'mp3', 'wav', 'ogg',
]);
const isRenderableExtension = (fileName) => {
    const ext = fileName?.split('.').pop()?.toLowerCase();
    return !!ext && RENDERABLE_EXTENSIONS.has(ext);
};

/** Genera un UID aleatorio corto */
const generateRandomUid = () => Math.random().toString(36).substr(2, 9);

/** Limpia el código de archivo en una ruta (elimina prefijo timestamp) */
const limpiarCodigoArchivoEnRuta = (fullPath) => {
    const partes = fullPath.split('/');
    const archivo = partes.pop();
    const nuevoNombre = archivo.replace(/^\d{13,}-/, '');
    return [...partes, nuevoNombre].join('/');
};

/** Limpia puntos de un código para usarlo en rutas */
const limpiarCodigo = (txt) => txt?.toString().replace(/\./g, '') ?? '';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTES DE NEGOCIO (estáticas, fuera del componente)
// ─────────────────────────────────────────────────────────────────────────────

const FOLDER_NAME_REGEX = /^[a-zA-Z0-9 _áéíóúÁÉÍÓÚñÑ.,()-´]+$/;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

const REGLAS_VALIDACION = {
    'tarea_134/indicador_16/medio_verificacion_15/': ['Homologación MER-ICO', 'Instrumento MER'],
    'tarea_418/indicador_77/medio_verificacion_72/': ['OTROSI'],
};

const PATHS_CON_ORDEN_FECHA = new Set([
    'tarea_221/indicador_34/medio_verificacion_29/',
    'tarea_214/indicador_30/medio_verificacion_25/',
    'tarea_223/indicador_36/medio_verificacion_31/',
    'tarea_234/indicador_43/medio_verificacion_38/',
    'tarea_313/indicador_52/medio_verificacion_47/',
    'tarea_323/indicador_58/medio_verificacion_53/',
]);

const PATH_CHECKLIST = new Set(['tarea_214/indicador_30/medio_verificacion_25/']);

// Rutas donde se habilita el filtro de coincidencias (soportes con el mismo
// nombre/cédula repetidos en una misma carpeta): Actas y Visitas de acompañamiento.
const RUTAS_FILTRO_COINCIDENCIAS = new Set([
    'tarea_214/indicador_30/medio_verificacion_25/',
    'tarea_221/indicador_34/medio_verificacion_29/',
]);

const INFORME_332_SEGMENTS = ['tarea_332', 'indicador_60', 'medio_verificacion_55'];

const OPCIONES_ESTADO = [
    'Sin categorizar', 'Infraestructura', 'Materiales',
    'Insumos', 'Insumos e infraestructura', 'No infraestructura',
];

const MESES = [
    'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
    'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

// ─────────────────────────────────────────────────────────────────────────────
// OPERACIONES INMUTABLES SOBRE EL ÁRBOL (puras, sin efectos secundarios)
// ─────────────────────────────────────────────────────────────────────────────
 
/** Ordena hijos: carpetas primero, luego numérico/alfaético */
const sortChildren = (node) => {
    if (!node.children?.length) return node;
    const extractLeadingNumber = (name) => {
        const m = name.match(/^(\d+)/);
        return m ? parseInt(m[0], 10) : null;
    };
    const sorted = [...node.children]
        .sort((a, b) => {
            if (a.type !== b.type) return a.type === 'folder' ? -1 : 1;
            const nA = extractLeadingNumber(a.name);
            const nB = extractLeadingNumber(b.name);
            if (nA !== null && nB !== null) return nA - nB;
            if (nA !== null) return -1;
            if (nB !== null) return 1;
            return a.name.localeCompare(b.name, undefined, { numeric: true });
        })
        .map(sortChildren);
    return { ...node, children: sorted };
};

/** Agrega un archivo a la rama correcta del árbol (inmutable) */
const addFileToTree = (nodes, targetPath, fileToAdd) =>
    nodes.map((node) => {
        if (node.path === targetPath) {
            const children = node.children ?? [];
            return {
                ...node,
                children: [
                    ...children,
                    {
                        name: fileToAdd.name,
                        key: `${node.key} - ${fileToAdd.uid}`,
                        type: 'file',
                        level: node.level + 1,
                        path: `${targetPath}/${fileToAdd.serverFileName}`,
                        pathFromClient: fileToAdd.pathFromClient,
                        uid: fileToAdd.uid,
                        serverFileName: fileToAdd.serverFileName,
                        viewName: fileToAdd.name,
                        typeS: 'Soporte',
                        isLeaf: true,
                        resourceType: fileToAdd.resourceType,
                        metadato: fileToAdd.metadato,
                    },
                ],
            };
        }
        if (node.children) {
            return { ...node, children: addFileToTree(node.children, targetPath, fileToAdd) };
        }
        return node;
    });

/** Elimina un nodo del árbol por su path (inmutable); devuelve {updatedTreeData, parentKey} */
const deleteNodeFromTree = (nodes, pathToDelete) => {
    let parentKey = null;
    const deleteRecursively = (ns) =>
        ns
            .map((node) => {
                if (node?.path === pathToDelete) return null;
                if (Array.isArray(node.children)) {
                    const updated = deleteRecursively(node.children);
                    if (updated.length === 0) parentKey = node.key;
                    return { ...node, children: updated };
                }
                return node;
            })
            .filter(Boolean);
    return { updatedTreeData: deleteRecursively(nodes), parentKey };
};

/** Elimina varios nodos de archivo del árbol por su serverFileName (inmutable) */
const deleteNodesByServerFileNames = (nodes, serverFileNamesSet) => {
    const deleteRecursively = (ns) =>
        ns
            .map((node) => {
                if (node.type === 'file' && serverFileNamesSet.has(node.serverFileName)) return null;
                if (Array.isArray(node.children)) {
                    return { ...node, children: deleteRecursively(node.children) };
                }
                return node;
            })
            .filter(Boolean);
    return deleteRecursively(nodes);
};

/** Agrega un nodo carpeta al árbol (inmutable) */
const addNodeToTree = (nodes, targetPath, newFolderName) =>
    nodes.map((node) => {
        if (node.path === targetPath) {
            const newNode = {
                name: newFolderName,
                key: `${targetPath}/${newFolderName}`,
                path: `${targetPath}/${newFolderName}`,
                type: 'folder',
                isLeaf: false,
                children: [],
                edit: true,
                canDelete: true,
                viewName: newFolderName,
                typeS: 'Desconocido',
            };
            return { ...node, children: [...(node.children ?? []), newNode] };
        }
        if (node.children) {
            return { ...node, children: addNodeToTree(node.children, targetPath, newFolderName) };
        }
        return node;
    });

/** Actualiza nombre y path de un nodo y sus hijos (inmutable) */
const updateNodeName = (nodes, keyToUpdate, newName, newPath) => {
    const updateChildrenPaths = (children, basePath) =>
        children.map((child) => {
            const childPath = `${basePath}/${child.name}`;
            return {
                ...child,
                path: childPath,
                key: childPath,
                children: child.children ? updateChildrenPaths(child.children, childPath) : child.children,
            };
        });

    return nodes.map((node) => {
        if (node.key === keyToUpdate) {
            return {
                ...node,
                name: newName,
                serverFileName: newName,
                viewName: newName,
                key: newPath,
                path: newPath,
                children: node.children ? updateChildrenPaths(node.children, newPath) : node.children,
            };
        }
        if (node.children) {
            return { ...node, children: updateNodeName(node.children, keyToUpdate, newName, newPath) };
        }
        return node;
    });
};

/** Actualiza un metadato puntual en un nodo sin clonar todo el árbol */
const updateNodeMetadata = (nodes, key, updater) =>
    nodes.map((node) => {
        if (node.key === key) return updater(node);
        if (node.children) {
            return { ...node, children: updateNodeMetadata(node.children, key, updater) };
        }
        return node;
    });

/** Busca un nodo por su key */
const findNodeByKey = (nodes, key) => {
    for (const node of nodes) {
        if (node.key === key) return node;
        if (node.children) {
            const found = findNodeByKey(node.children, key);
            if (found) return found;
        }
    }
    return null;
};

/** Filtra carpetas vacías */
const filterEmptyFolders = (nodes) => {
    const isEmptyFolder = (node) => {
        if (node.type !== 'folder') return false;
        if (!node.children?.length) return true;
        return node.children.every(isEmptyFolder);
    };
    return nodes
        .map((node) => {
            const filtered = node.children ? filterEmptyFolders(node.children) : [];
            if (node.type === 'folder') {
                const keep = isEmptyFolder(node) || filtered.length > 0;
                return keep ? { ...node, children: filtered } : null;
            }
            return null;
        })
        .filter(Boolean);
};

/** Filtra carpetas que tengan al menos un archivo */
const filterFoldersWithFiles = (nodes, isRoot = false) => {
    const hasFiles = (node) => {
        if (node.type === 'file') return true;
        return node.children?.some(hasFiles) ?? false;
    };
    return nodes
        .map((node) => {
            const filtered = node.children ? filterFoldersWithFiles(node.children) : [];
            if (node.type === 'folder') {
                const keep = isRoot || hasFiles(node);
                return keep ? { ...node, children: filtered } : null;
            }
            return node;
        })
        .filter(Boolean);
};

/** Ordena archivos por fecha (para medios de verificación específicos) */
const ordenarPorFechaAscendente = (archivos) => {
    const limpiarNombre = (name) => {
        const partes = name.split('-');
        return partes.length < 2 ? name : partes.slice(1).join('-');
    };
    const getFechaArchivo = (nameLimpio) => {
        const m = nameLimpio.match(/^(\d{8})_/);
        if (!m) return null;
        const raw = m[1];
        return new Date(`${raw.slice(4, 8)}-${raw.slice(2, 4)}-${raw.slice(0, 2)}T00:00:00`);
    };
    const parsearFechaCarpeta = (nombre) => {
        const m = nombre.match(/([a-zA-ZñÑ]+).*?(\d{4})/);
        if (!m) return null;
        const mes = m[1].toLowerCase();
        const year = parseInt(m[2]);
        let mesIndex = MESES.findIndex((me) => mes.startsWith(me));
        if (mesIndex === -1) mesIndex = 0;
        return { year, mes: mesIndex };
    };
    const rutaObjetivo = 'medio_verificacion_25/';

    return archivos
        .map((item) =>
            item.type === 'folder' && Array.isArray(item.children)
                ? { ...item, children: ordenarPorFechaAscendente(item.children) }
                : item
        )
        .sort((a, b) => {
            if (a.type === 'folder' && b.type !== 'folder') return -1;
            if (a.type !== 'folder' && b.type === 'folder') return 1;
            if (a.type === 'folder' && b.type === 'folder') {
                if ((a.path || '').includes(rutaObjetivo)) {
                    const fA = parsearFechaCarpeta(a.name);
                    const fB = parsearFechaCarpeta(b.name);
                    if (fA && fB) {
                        if (fA.year !== fB.year) return fA.year - fB.year;
                        return fA.mes - fB.mes;
                    }
                }
                return a.name.localeCompare(b.name, 'es', { numeric: true, sensitivity: 'base' });
            }
            const nA = limpiarNombre(a.name);
            const nB = limpiarNombre(b.name);
            const dA = getFechaArchivo(nA);
            const dB = getFechaArchivo(nB);
            if (dA && dB) {
                const diff = dA - dB;
                return diff !== 0 ? diff : nA.substring(9).localeCompare(nB.substring(9), 'es', { numeric: true });
            }
            if (!dA && !dB) return nA.localeCompare(nB, 'es', { numeric: true });
            return dA ? -1 : 1;
        });
};

/**
 * Extrae la cédula/identificador desde el nombre de un soporte para detectar
 * coincidencias (mismo criterio que el backend: prefijo "C" opcional, últimos
 * 6-12 dígitos, descartando fechas ddmmyyyy).
 */
const extraerIdentificadorSoporte = (nombreArchivo) => {
    if (!nombreArchivo) return null;
    let limpio;
    try {
        limpio = decodeURIComponent(nombreArchivo);
    } catch {
        limpio = nombreArchivo;
    }
    limpio = limpio
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .replace(/^\d{10,}[-_]/, '')
        .replace(/(\.[a-z0-9]+)+$/gi, '')
        .replace(/\s*\([^)]*\)\s*/g, ' ')
        .replace(/(?:^|[\s_\-.])C(\d+)/gi, '$1')
        .replace(/[._-]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();

    const matches = Array.from(limpio.matchAll(/\d{6,12}/g)).map((m) => m[0]);
    if (!matches.length) return null;

    const validos = matches.filter((num) => {
        if (num.length !== 8) return true;
        const dd = Number(num.slice(0, 2));
        const mm = Number(num.slice(2, 4));
        const yyyy = Number(num.slice(4, 8));
        return !(dd >= 1 && dd <= 31 && mm >= 1 && mm <= 12 && yyyy >= 1900 && yyyy <= 2100);
    });
    if (!validos.length) return null;

    return validos[validos.length - 1];
};

/**
 * Agrupa los soportes de una carpeta por su identificador y, según el filtro
 * de coincidencias, deja solo los repetidos o solo los únicos, reordenándolos
 * para que los que comparten identificador queden uno debajo del otro.
 * Las carpetas nunca se filtran, solo los archivos.
 */
const aplicarFiltroCoincidencias = (items, filtro) => {
    if (!Array.isArray(items) || filtro === 'todos') return items;

    const carpetas = items.filter((i) => i.type === 'folder');
    const archivos = items.filter((i) => i.type !== 'folder');

    const grupos = new Map();
    archivos.forEach((archivo) => {
        const identificador = extraerIdentificadorSoporte(archivo.viewName || archivo.name) ?? `__sin_identificar_${archivo.key}`;
        if (!grupos.has(identificador)) grupos.set(identificador, []);
        grupos.get(identificador).push(archivo);
    });

    const resultado = [];
    grupos.forEach((grupo) => {
        const esRepetido = grupo.length > 1;
        if ((filtro === 'repetidos' && esRepetido) || (filtro === 'unicos' && !esRepetido)) {
            resultado.push(...grupo);
        }
    });

    return [...carpetas, ...resultado];
};

/** Indica si una carpeta del árbol pertenece a alguna de las rutas con filtro de coincidencias */
const rutaConFiltroCoincidencias = (path) =>
    Array.from(RUTAS_FILTRO_COINCIDENCIAS).some((r) => (path || '').includes(r));

/**
 * Recorre todo el árbol y, en cada carpeta que pertenezca a una ruta con
 * filtro de coincidencias habilitado, aplica el filtro sobre sus soportes.
 * El resto del árbol se deja intacto.
 */
const aplicarFiltroCoincidenciasEnArbol = (nodes, filtro) => {
    if (!Array.isArray(nodes) || filtro === 'todos') return nodes;
    return nodes.map((node) => {
        if (node.type !== 'folder' || !Array.isArray(node.children)) return node;
        const hijosRecursivos = aplicarFiltroCoincidenciasEnArbol(node.children, filtro);
        const hijosFinal = rutaConFiltroCoincidencias(node.path)
            ? aplicarFiltroCoincidencias(hijosRecursivos, filtro)
            : hijosRecursivos;
        return { ...node, children: hijosFinal };
    });
};

/** Filtra archivos validados con estado infraestructura (P25) */
const filterP25AndValidFiles = (tree) => {
    const countValidFiles = (nodes) => {
        let count = 0;
        for (const n of nodes) {
            if (n.type === 'file') count += 1;
            else if (n.type === 'folder' && n.children) count += countValidFiles(n.children);
        }
        return count;
    };
    return tree
        .map((node) => {
            if (node.type === 'file') {
                const esValidado = node.metadato?.estadovalidacion === 'Validado';
                const estado = node.metadato?.estado;
                const estadoValido = estado === 'Infraestructura' || estado === 'Insumos e infraestructura';
                return esValidado && estadoValido ? node : null;
            }
            if (node.type === 'folder' && Array.isArray(node.children)) {
                const filtered = filterP25AndValidFiles(node.children);
                if (!filtered.length) return null;
                return { ...node, children: filtered, fileCount: countValidFiles(filtered) };
            }
            return null;
        })
        .filter(Boolean);
};

/** Construye la estructura de informes para tarea de visitas (informeVisitas) */
const obtenerInformesDesde12ConProyecto = (data, proyectoCode) => {
    const informes = [];
    const limiteInicio = proyectoCode === 'P25' ? 9 : 12;
    data.forEach((nodo) => {
        if (!Array.isArray(nodo.children)) return;
        nodo.children.forEach((informe) => {
            const match = (informe.name || '').match(/Informe\s+(\d+)/i);
            if (!match || parseInt(match[1], 10) < limiteInicio) return;
            const soporteInfra = informe.children?.find((c) =>
                normalize(c.name).includes('soportes infraestructura')
            );
            if (!soporteInfra) return;
            const implementacion = soporteInfra.children?.find(
                (c) => normalize(c.name) === 'implementacion'
            );
            if (!implementacion) return;
            const fao = implementacion.children?.find((c) => normalize(c.name) === 'fao');
            if (!fao) return;
            const proyectoFolder = fao.children?.find((c) =>
                normalize(c.name).startsWith(normalize(proyectoCode))
            );
            if (!proyectoFolder) return;
            const archivos = (proyectoFolder.children || []).filter((c) => c.type === 'file');
            informes.push({ ...informe, children: [proyectoFolder], fileCount: archivos.length });
        });
    });
    return informes;
};

// ─────────────────────────────────────────────────────────────────────────────
// HOOK PARA CALCULAR CODACTIVIDADPARAMETRO (evita repetición en todo el módulo)
// ─────────────────────────────────────────────────────────────────────────────
const useCodActividad = (codigo_tarea, rutaActividad) =>
    useMemo(() => {
        const rawCodActividad = codigo_tarea?.codigo_tarea?.codActividad;
        if (rawCodActividad) return 'actividad_' + limpiarCodigo(rawCodActividad);
        if (rutaActividad) return rutaActividad.replace(/^actividad_/, '');
        return '';
    }, [codigo_tarea, rutaActividad]);

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

const FileManagerPOA = ({ codigo_tarea, onClose, onUploaded }) => {
    // ── Parámetros de tarea ───────────────────────────────────────────────────
    const tmpCodigo = limpiarCodigo(codigo_tarea.codigo_tarea.codigo_tarea);
    const codigo_proyecto_busqueda = 'tarea_' + tmpCodigo;
    const pasos = codigo_tarea.codigo_tarea.paso;
    const proyecto = codigo_tarea.codigo_tarea.proyecto || codigo_tarea.codigo_tarea.proyectoBuscar;
    const proyecto25 = codigo_tarea.codigo_tarea.proyecto25;
    const informeVisitas = codigo_tarea.codigo_tarea.informeVisitas;
    const proyectoNoLinkeado = codigo_tarea.codigo_tarea.proyectoNoLinkeado;
    const rutaActividad = codigo_tarea.codigo_tarea.rutaActividad;
    const zonaProyecto = codigo_tarea.codigo_tarea.banderaZonas;
    const poaConExtension = codigo_tarea.codigo_tarea.poaConExtension;
    const banderaCarpetasArchivos = codigo_tarea.codigo_tarea.banderaCarpetasArchivos || false;
    const filtrarrutaNew = codigo_tarea.codigo_tarea.proyectoEdah || false;
    const nuevaRuta = codigo_tarea.codigo_tarea.nuevaRuta || false;
    const rutaInformacionGeneral = codigo_tarea.codigo_tarea.rutaInformacionGeneral || false;
    const codActividadParametro = useCodActividad(codigo_tarea, rutaActividad);

    // Ruta de reemplazo
    const dataTask = codigo_tarea?.codigo_tarea ?? codigo_tarea;
    const oidMV = dataTask?.medioV?.[0]?.oidMV;
    const remplazo_ruta = `DocumentosPOA/actividad_${limpiarCodigo(dataTask.codActividad)}/tarea_${limpiarCodigo(dataTask.codigo_tarea)}/${dataTask.key}/medio_verificacion_${oidMV}/`;

    // Descripción cabecera
    const { codActividad, nameActividad, codigo_tarea: codigoTareaDesc, tarea, indicador } = codigo_tarea.codigo_tarea;
    const medioVerificacion = useMemo(() => {
        if (!Array.isArray(codigo_tarea.codigo_tarea.medioV)) return 'Medio de Verificación';
        return codigo_tarea.codigo_tarea.medioV.map((i) => i.medioVerificacion).join(', ');
    }, [codigo_tarea]);

    // ── Redux ─────────────────────────────────────────────────────────────────
    const user = useSelector((state) => state.auth.user);
    const userLogin = user.username;

    // Permisos
    const permissions = useSelector((state) => selectAppPermissions(state, 'file-managerPOA'));
    const permissionAccess = permissions.priv_access === 'Y';
    const permissionDelete = permissions.priv_delete === 'Y';
    const permissionUpdate = permissions.priv_update === 'Y';
    const permissionInsert = permissions.priv_insert === 'Y';
    const permissionExport = permissions.priv_export === 'Y';
    const permissionPrint = permissions.priv_print === 'Y';

    const permissions2 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-check'));
    const permissionUpdate2 = permissions2.priv_update === 'Y';
    const permissionAccess2 = permissions2.priv_access === 'Y';
    const permissionDelete2 = permissions2.priv_delete === 'Y';

    const permissions3 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-File-All'));
    const permissionAccess3 = permissions3.priv_access === 'Y';
    const permissionInsert3 = permissions3.priv_insert === 'Y';
    const permissionUpdate3 = permissions3.priv_update === 'Y';
    const permissionDelete3 = permissions3.priv_delete === 'Y';
    const permissionPrint3 = permissions3.priv_print === 'Y';

    const permissions4 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-Entrega'));
    const permissionAccess4 = permissions4.priv_access === 'Y';
    const permissionUpdate4 = permissions4.priv_update === 'Y';

    const permissions5 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-Vaidado'));
    const permissionAccess5 = permissions5.priv_access === 'Y';
    const permissionUpdate5 = permissions5.priv_update === 'Y';

    const permissions6 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-codigo'));
    const permissionAccess6 = permissions6.priv_access === 'Y';

    const permissions7 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-Reemplazo'));
    const permissionAccess7 = permissions7.priv_access === 'Y';
    const permissionInsert7 = permissions7.priv_insert === 'Y';
    
    // Control de permisos para el filtro de coincidencias
    const permission8 = useSelector((state) => selectAppPermissions(state, 'file-managerPOA-Filter'));
    const permissionAccess8 = permission8.priv_access === 'Y';

    // Control de permisos para subir soportes sin fecha de creación
    // La tarea 2.1.4 (tarea_214) queda excluida a propósito: aunque el usuario
    // tenga el permiso, en esta tarea la fecha de creación siempre es obligatoria.
    const permissionsFechaPoa = useSelector((state) => selectAppPermissions(state, 'fechaPoa'));
    const permissionAccessFechaPoa = permissionsFechaPoa.priv_access === 'Y' && codigo_proyecto_busqueda !== 'tarea_214';

    // ── Estado del árbol ──────────────────────────────────────────────────────
    const [treeData, setTreeData] = useState([]);
    const [expandedKeys, setExpandedKeys] = useState([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);
    const treeInitialized = useRef(false); // ← FIX: evita reset de navegación

    // ── Estado de selección ───────────────────────────────────────────────────
    const [selectedItem, setSelectedItem] = useState(null);
    const [rootNodeSelected, setRootNodeSelected] = useState(true);
    const [isFinalFolder, setIsFinalFolder] = useState(false);

    // ── Estado de edición ─────────────────────────────────────────────────────
    const [newName, setNewName] = useState('');
    const [isEditing, setIsEditing] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');

    // ── Estado de UI ──────────────────────────────────────────────────────────
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [showDescriptions, setShowDescriptions] = useState(true);
    const [activeButton, setActiveButton] = useState(null);
    const [hover, setHover] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    // ── Estados de carga ──────────────────────────────────────────────────────
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { startDownload, isDownloading } = useDownloadManager();

    // ── Estados de advertencias/mensajes ──────────────────────────────────────
    const [showWarning, setShowWarning] = useState(false);
    const [controlMensaje, setControlMensaje] = useState(false);
    const [controlMensaje34, setControlMensaje34] = useState(false);
    const [mostrarPrimerTexto, setMostrarPrimerTexto] = useState(false);
    const [mostrarSegundoTexto, setMostrarSegundoTexto] = useState(false);
    const [instrumento, setInstrumento] = useState(false);
    const [mostrarCheckbox, setMostrarCheckbox] = useState(false);
    const [estadoActEnt, setEstadoActEnt] = useState(false);
    const [codigoTareaTable, setCodigoTareaTable] = useState(false);
    const [mostrarFiltroCoincidencias, setMostrarFiltroCoincidencias] = useState(false);
    const [filtroCoincidencias, setFiltroCoincidencias] = useState('todos');
    const [sinFechaFile, setSinFechaFile] = useState(false);
    const [sinFechaMultiple, setSinFechaMultiple] = useState(false);

    // ── Modales ───────────────────────────────────────────────────────────────
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isModalFileVisible, setIsModalFileVisible] = useState(false);
    const [isModalVisibleAllFile, setIsModalVisibleAllFile] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);

    // Editar nombre de un soporte (conservando el código del servidor, ver handleRenameFile)
    const [renameFileModalVisible, setRenameFileModalVisible] = useState(false);
    const [renameFileTarget, setRenameFileTarget] = useState(null);
    const [renameFileValue, setRenameFileValue] = useState('');
    const [renameFileExtension, setRenameFileExtension] = useState('');
    const [isRenamingFile, setIsRenamingFile] = useState(false);

    // ── Archivos ──────────────────────────────────────────────────────────────
    const [fileToUpload, setFileToUpload] = useState(null);
    const [fileList, setFileList] = useState([]);
    const [fileListAllFile, setFileListAllFile] = useState([]);
    const [fileInsetAll, setFileInsetAll] = useState(false);
    const [nameFileAll, setNameFileAll] = useState('');

    // ── Paginación ────────────────────────────────────────────────────────────
    const [tableParams, setTableParams] = useState({
        pagination: { current: 1, pageSize: 30, total: 0 },
    });

    // ── Recarga y contexto EDAH ───────────────────────────────────────────────
    const [recargarKey, setRecargarKey] = useState(0);
    const [proyectoRutaNew, setProyectoRutaNew] = useState(null);
    const esInformeEDAH = !!proyectoRutaNew;

    // ── Reemplazo ─────────────────────────────────────────────────────────────
    const [selectItemRemplazo, setSelectItemRemplazo] = useState(null);

    // ── Informe 332 ───────────────────────────────────────────────────────────
    const [informe332Visible, setInforme332Visible] = useState(false);
    const [openInforme332, setOpenInforme332] = useState(false);

    // ── Forms y refs ──────────────────────────────────────────────────────────
    const [form] = Form.useForm();
    const [formAllFile] = Form.useForm();
    const inputRef = useRef(null);

    // typeS formateado del item seleccionado
    const formattedTypeS = useMemo(() => {
        const typeS = selectedItem?.typeS || '';
        return typeS
            .replace(/_/g, ' ')
            .toLowerCase()
            .replace(/^\w/, (c) => c.toUpperCase());
    }, [selectedItem]);

    // ─────────────────────────────────────────────────────────────────────────
    // HELPER INTERNO: construye el logEntry (wrapper mínimo de logAction)
    // ─────────────────────────────────────────────────────────────────────────
    const handleAction = useCallback(({ user: u, actionType, nameServer, resourceDetails }) =>
        logAction(u, actionType, nameServer, resourceDetails),
        []);

    // ─────────────────────────────────────────────────────────────────────────
    // CIERRE DEL DRAWER
    // ─────────────────────────────────────────────────────────────────────────
    const handleClose = useCallback(() => {
        setIsVisible(false);
        onClose();
        setTreeData([]);
        setExpandedKeys([]);
        treeInitialized.current = false;
    }, [onClose]);

    // ─────────────────────────────────────────────────────────────────────────
    // CARGA DE DATOS (fetch inicial + recargas)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        treeInitialized.current = false; // reset antes de cada carga nueva
        const fetchData = async () => {
            try {
                let rawData;
                const filterNodes = (nodes, condition) =>
                    nodes.flatMap((node) =>
                        condition(node)
                            ? [node]
                            : node.children
                                ? filterNodes(node.children, condition)
                                : []
                    );

                if (nuevaRuta) {
                    const res = await axios.get(
                        `${baseUrl}/api/directorytreepoa?nuevaRuta=${rutaInformacionGeneral}`
                    );
                    let filtered = res.data.map(sortChildren);
                    if (filtrarrutaNew !== 'todos') {
                        filtered = filterNodes(filtered, (n) => n.name.includes(filtrarrutaNew));
                    }
                    setTreeData(filtered);
                    return;
                }

                if (!codigo_tarea.codigo_tarea.codigo_tarea) {
                    const res = await axios.get(
                        `${baseUrl}/api/directorytreepoa?codigActividad=${rutaActividad}&codigoTarea=${codigo_tarea.codigo_tarea.nombre}`
                    );
                    let filtered = res.data.map(sortChildren);
                    if (codigo_tarea.codigo_tarea.linkeado === 2) {
                        filtered = filterNodes(filtered, (n) => n.name.includes(proyectoNoLinkeado));
                    }
                    setTreeData(filtered);
                    return;
                }

                if (!codigo_proyecto_busqueda) {
                    setShowWarning(true);
                    return;
                }

                const tmpcodAct = limpiarCodigo(codigo_tarea.codigo_tarea.codActividad);
                const codActParam = 'actividad_' + tmpcodAct;

                if (codigo_tarea.codigo_tarea.dataInfraestructura) {
                    const sanitize = (nodes) =>
                        Array.isArray(nodes)
                            ? nodes.map((n) => ({ ...n, children: sanitize(n.children || []) }))
                            : [];
                    setTreeData(sanitize(codigo_tarea.codigo_tarea.dataInfraestructura));
                    return;
                }

                const params = {
                    codigoTarea: codigo_proyecto_busqueda,
                    codigActividad: codActParam,
                };
                if (codigo_tarea.codigo_tarea.rutaEstructuracion) {
                    params.rutaEstructuracion = codigo_tarea.codigo_tarea.rutaEstructuracion;
                }
                const res = await axios.get(
                    `${baseUrl}/api/directorytreepoa?${new URLSearchParams(params).toString()}`
                );
                rawData = res.data;

                const indicadorKey = codigo_tarea.codigo_tarea.key;
                // Mensajes de control según tarea/indicador
                if (codigo_proyecto_busqueda === 'tarea_221') {
                    setControlMensaje(indicadorKey === 'indicador_34');
                    setControlMensaje34(false);
                    setMostrarPrimerTexto(indicadorKey === 'indicador_34');
                } else if (indicadorKey === 'indicador_30') {
                    setControlMensaje34(true);
                    setControlMensaje(false);
                    setMostrarSegundoTexto(true);
                } else {
                    setControlMensaje(false);
                    setControlMensaje34(false);
                }

                const getIndicadorChildren = (nodes) =>
                    nodes.flatMap((node) =>
                        node.typeS === 'indicador' && node.name === indicadorKey
                            ? node.children
                            : node.children
                                ? getIndicadorChildren(node.children)
                                : []
                    );

                let filteredData = getIndicadorChildren(rawData);

                if (zonaProyecto) {
                    filteredData = filterNodes(filteredData, (n) => n.name.includes(zonaProyecto));
                } else if (banderaCarpetasArchivos) {
                    filteredData = filterNodes(rawData, (n) => n.name.includes(null)); // mantiene lógica original
                } else if (codigo_tarea.codigo_tarea.linkeado === 1) {
                    filteredData = filterNodes(filteredData, (n) =>
                        n.name.includes(codigo_tarea.codigo_tarea.ruta)
                    );
                } else if (pasos) {
                    filteredData = filterNodes(
                        filterNodes(filteredData, (n) => n.name.includes(proyecto)),
                        (n) => n.name === pasos
                    );
                } else if (poaConExtension) {
                    const ext = filterNodes(rawData, (n) => n.name === 'POA con Extensión');
                    filteredData = filterNodes(ext, (n) => n.name.includes(proyecto));
                } else if (proyecto) {
                    filteredData = filterNodes(filteredData, (n) => n.name.includes(proyecto));
                } else if (proyecto25) {
                    filteredData = filterP25AndValidFiles(
                        filterNodes(filteredData, (n) => n.name.includes(proyecto25))
                    );
                    if (!filteredData?.length) {
                        message.warning(
                            'No se encontraron archivos validados de infraestructura en la carpeta seleccionada.'
                        );
                        handleClose();
                        return;
                    }
                } else if (informeVisitas) {
                    const carpetaFinal = informeVisitas.split('/').pop();
                    filteredData = obtenerInformesDesde12ConProyecto(filteredData, carpetaFinal);
                }

                if (!filteredData?.length) filteredData = rawData;
                filteredData = ordenarPorFechaAscendente(filteredData);
                setTreeData(filteredData);
            } catch (error) {
                console.error('Error fetching data:', error);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [codigo_tarea, recargarKey]);

    // ─────────────────────────────────────────────────────────────────────────
    // FIX PRINCIPAL: solo selecciona el primer nodo en la carga inicial,
    // NO en actualizaciones posteriores (subida/eliminación/edición de archivos)
    // ─────────────────────────────────────────────────────────────────────────
    useEffect(() => {
        if (treeData.length > 0 && !treeInitialized.current) {
            treeInitialized.current = true;
            const firstNode = treeData[0];
            handleSelect([firstNode.key || firstNode.path], { node: firstNode });
        }
        // Siempre asegurar que la clave raíz esté en expandedKeys
        if (treeData.length > 0) {
            setExpandedKeys((prev) => {
                const rootKey = treeData[0]?.key;
                return prev.includes(rootKey) ? prev : [...prev, rootKey];
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [treeData]);

    // Foco en input de nueva carpeta al abrir modal
    useEffect(() => {
        if (isModalVisible && inputRef.current) inputRef.current.focus();
    }, [isModalVisible]);

    // ─────────────────────────────────────────────────────────────────────────
    // HANDLERS DEL ÁRBOL
    // ─────────────────────────────────────────────────────────────────────────
    const onExpand = useCallback((keys) => {
        setExpandedKeys(keys);
        setAutoExpandParent(false); // no forzar re-expansión del padre
    }, []);

    const handleSelect = useCallback(
        async (selectedKeys, info) => {
            // Resuelve siempre el nodo original (sin filtrar) desde treeData,
            // ya que el árbol visible puede estar mostrando la versión filtrada
            // por coincidencias y no debe perderse información al seleccionar.
            const selectedNode = findNodeByKey(treeData, info.node.key) || info.node;
            const nombreNodoRaiz = codigo_proyecto_busqueda;
            const basePath = selectedNode.path.split('/').slice(0, 3).join('/') + '/';
            const esInstrumento =
                selectedNode.path.includes('tarea_419')
                    ? true
                    : REGLAS_VALIDACION[basePath]?.includes(selectedNode.serverFileName) || false;

            setFileInsetAll(true);

            if (nuevaRuta) {
                setProyectoRutaNew(basePath);
            } else if (basePath.includes('tarea_214')) {
                setEstadoActEnt(true);
                setNameFileAll('Actas_entrega');
            } else if (basePath.includes('tarea_221')) {
                setEstadoActEnt(true);
                setNameFileAll('Visitas_de_acompañamiento');
            } else if (basePath.includes('tarea_223')) {
                setNameFileAll('Informe');
            }

            setInstrumento(esInstrumento);

            const path = selectedNode?.path || '';
            const tieneRutaValida = Array.from(PATH_CHECKLIST).some((r) => path.includes(r));
            const isInforme332 = INFORME_332_SEGMENTS.every((seg) => path.includes(seg));
            setInforme332Visible(isInforme332);
            setMostrarCheckbox(tieneRutaValida && permissionAccess2);
            setCodigoTareaTable(tieneRutaValida && permissionAccess6);
            setMostrarFiltroCoincidencias(Array.from(RUTAS_FILTRO_COINCIDENCIAS).some((r) => path.includes(r)));

            // Ordenar hijos si la ruta lo requiere
            let node = selectedNode;
            if ([...PATHS_CON_ORDEN_FECHA].some((p) => path.includes(p)) && Array.isArray(node.children)) {
                node = { ...node, children: ordenarPorFechaAscendente(node.children) };
            }

            // Determinar si es carpeta final (sin sub-carpetas)
            const hasFolderChild = node.children?.some((c) => c.type === 'folder') ?? false;
            setIsFinalFolder(!hasFolderChild || !node.children?.length);

            if (node.name === nombreNodoRaiz) {
                setRootNodeSelected(true);
                setSelectedItem(null);
            } else {
                const nodePath = node.path && node.path !== 'undefined' ? node.path : null;
                setSelectedItem({ ...node, path: nodePath, pathFromClient: nodePath });
                setRootNodeSelected(false);
                if (node.type === 'file') fetchFileUrl(node);
                setTableParams((prev) => ({
                    ...prev,
                    pagination: { ...prev.pagination, total: node.children?.length ?? 0, current: 1 },
                }));
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [codigo_proyecto_busqueda, nuevaRuta, permissionAccess2, permissionAccess6, treeData]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // BÚSQUEDA EN EL ÁRBOL
    // ─────────────────────────────────────────────────────────────────────────
    const onClickBuscar = useCallback(() => {
        if (!searchValue.trim()) return;
        const term = searchValue.toLowerCase();

        const searchInTree = (node, expandedAcc) => {
            let found = false;
            const newNode = { ...node };
            if (node.viewName?.toLowerCase().includes(term)) {
                newNode.highlighted = true;
                found = true;
            } else {
                newNode.highlighted = false;
            }
            if (node.children?.length) {
                const { children: updatedChildren, anyFound } = node.children.reduce(
                    (acc, child) => {
                        const { node: cn, found: cf, expandedKeys: ek } = searchInTree(child, acc.expandedKeys);
                        if (cf) expandedAcc.push(node.key);
                        return { children: [...acc.children, cn], anyFound: acc.anyFound || cf, expandedKeys: ek };
                    },
                    { children: [], anyFound: false, expandedKeys: expandedAcc }
                );
                newNode.children = updatedChildren;
                found = found || anyFound;
            }
            return { node: newNode, found, expandedKeys: expandedAcc };
        };

        const newExpandedKeys = [];
        const newTree = treeData.map((root) => {
            const { node } = searchInTree(root, newExpandedKeys);
            return node;
        });
        const rootKey = treeData[0]?.key;
        if (rootKey && !newExpandedKeys.includes(rootKey)) newExpandedKeys.push(rootKey);
        setTreeData(newTree);
        setExpandedKeys(newExpandedKeys);
    }, [searchValue, treeData]);

    const onLimpiarBusqueda = useCallback(() => {
        setActiveButton(null);
        setSearchValue('');
        setRootNodeSelected(true);
        setSelectedItem(null);
        setLoading(true);
        setShowWarning(false);
        setControlMensaje(false);
        setControlMensaje34(false);
        setMostrarPrimerTexto(false);
        setMostrarSegundoTexto(false);
        setRecargarKey((prev) => prev + 1);
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // SINCRONIZACIÓN CON EL MODAL "GESTIÓN DE ARCHIVOS" (PoaEliminar)
    // Quita del árbol y de selectedItem los archivos borrados allí, sin
    // recargar todo el árbol desde el backend.
    // ─────────────────────────────────────────────────────────────────────────
    const handleFilesDeletedFromModal = useCallback((archivosEliminados) => {
        if (!archivosEliminados?.length) return;
        const serverFileNames = new Set(
            archivosEliminados.map((f) => (f.rutaConCodigo || f.ruta || '').split('/').pop())
        );
        setTreeData((prev) => deleteNodesByServerFileNames(prev, serverFileNames));
        setSelectedItem((prev) => {
            if (!prev?.children) return prev;
            return {
                ...prev,
                children: prev.children.filter((c) => !serverFileNames.has(c.serverFileName)),
            };
        });
        onUploaded?.();
    }, [onUploaded]);

    // ─────────────────────────────────────────────────────────────────────────
    // FILTROS DEL ÁRBOL
    // ─────────────────────────────────────────────────────────────────────────
    const handleFilterEmptyFolders = useCallback(() => {
        setTreeData((prev) => filterEmptyFolders(prev).map(sortChildren));
        setActiveButton('emptyFolders');
    }, []);

    const handleFilterFolders = useCallback(() => {
        setTreeData((prev) => filterFoldersWithFiles(prev, true).map(sortChildren));
        setActiveButton('folders');
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // EDICIÓN DE NOMBRE DE CARPETA
    // ─────────────────────────────────────────────────────────────────────────
    const toggleEditMode = useCallback(() => {
        setIsEditing((prev) => {
            if (!prev) setNewName(selectedItem?.viewName ?? '');
            return !prev;
        });
    }, [selectedItem]);

    const cancelEditMode = useCallback(() => {
        setIsEditing(false);
        setNewName(selectedItem?.viewName ?? '');
    }, [selectedItem]);

    const handleEditNameChange = useCallback((e) => {
        const value = e.target.value.normalize('NFC');
        if (FOLDER_NAME_REGEX.test(value) || value === '') {
            setNewName(value);
        } else {
            message.error('El nombre contiene caracteres no permitidos.');
        }
    }, []);

    const handleNameChange = useCallback((e) => {
        const value = e.target.value.normalize('NFC');
        if (FOLDER_NAME_REGEX.test(value) || value === '') {
            setNewFolderName(value);
        } else {
            message.error('El nombre contiene caracteres no permitidos.');
        }
    }, []);

    const handleKeyDown = useCallback(
        (e) => { if (e.key === 'Enter') handleCreateFolder(); },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [newFolderName, selectedItem]
    );

    const handleEditFolder = useCallback(
        async (path, nuevoNombre) => {
            if (!FOLDER_NAME_REGEX.test(nuevoNombre)) {
                message.error('El nombre contiene caracteres no permitidos.');
                return;
            }
            if (!selectedItem) {
                message.warning('No hay carpeta seleccionada para editar.');
                return;
            }
            const nodeRaiz = 'DocumentosPOA';
            const tieneActividad = !!codActividadParametro;
            const tieneProyectoEDAH = !!proyectoRutaNew;
            let oldPath = '';
            let tmpRuta = '';
            let fatherResource = '';
            try {
                if (tieneActividad) {
                    const lastSlash = path.lastIndexOf('/');
                    const basePath = lastSlash >= 0 ? path.substring(0, lastSlash) : '';
                    const noActiv = !path.includes('actividad_');
                    tmpRuta = noActiv
                        ? `${codActividadParametro}/${basePath ? `${basePath}/` : ''}${nuevoNombre}`.replace(/\/{2,}/g, '/')
                        : path.replace(/[^/]+$/, nuevoNombre);
                    oldPath = (noActiv ? `${codActividadParametro}/${path}` : path).replace(/\/{2,}/g, '/');
                    const fSlash = tmpRuta.lastIndexOf('/');
                    fatherResource = fSlash >= 0 ? tmpRuta.substring(0, fSlash) : '';
                } else if (tieneProyectoEDAH) {
                    const cleanP = (proyectoRutaNew || '').replace(/\/$/, '').trim();
                    if (path.includes('/')) {
                        const lastSlash = path.lastIndexOf('/');
                        const bp = path.substring(0, lastSlash);
                        const seg = path.substring(lastSlash + 1);
                        oldPath = `${rutaInformacionGeneral}/${cleanP}/${bp ? `${bp}/` : ''}${seg}`.replace(/\/{2,}/g, '/');
                        tmpRuta = `${rutaInformacionGeneral}/${cleanP}/${bp ? `${bp}/` : ''}${nuevoNombre}`.replace(/\/{2,}/g, '/');
                        fatherResource = `${rutaInformacionGeneral}/${cleanP}/${bp}`.replace(/\/$/, '');
                    } else {
                        oldPath = `${rutaInformacionGeneral}/${path}`;
                        tmpRuta = `${rutaInformacionGeneral}/${nuevoNombre}`;
                        fatherResource = rutaInformacionGeneral;
                    }
                }
                if (!tmpRuta || !oldPath) {
                    message.warning('No se pudo determinar la ruta para editar.');
                    return;
                }
                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'updateFolder',
                    nameServer: nuevoNombre,
                    resourceDetails: {
                        nameResource: nuevoNombre,
                        Path: tmpRuta,
                        fatherResource: `${nodeRaiz}/${fatherResource}`.replace(/\/{2,}/g, '/'),
                        typeResource: 'folder',
                        key: `${nodeRaiz}/${tmpRuta}`.replace(/\/{2,}/g, '/'),
                        fullPath: `${nodeRaiz}/${tmpRuta}`.replace(/\/{2,}/g, '/'),
                        resourceType: 'Folder POA',
                    },
                });
                const fd = new FormData();
                fd.append('ruta', oldPath);
                fd.append('nuevoNombre', nuevoNombre);
                fd.append('nodeRaiz', nodeRaiz);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));
                const res = await axios.post(`${baseUrl}/api/editNameDirectories`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data; charset=UTF-8' },
                });
                if (res.status === 200) {
                    const newPath = tieneActividad
                        ? (res.data?.nuevoPath || tmpRuta).replace(/actividad_\d+\//, '')
                        : res.data?.nuevoPath || tmpRuta;
                    setTreeData((prev) => updateNodeName(prev, selectedItem.key, nuevoNombre, newPath));
                    setSelectedItem((prev) =>
                        prev ? { ...prev, name: nuevoNombre, key: newPath, path: newPath, pathFromClient: newPath, viewName: nuevoNombre } : prev
                    );
                    setIsEditing(false);
                    if (res.data?.cascadeError) {
                        message.warning(
                            res.data.message ||
                            'La carpeta se renombró, pero algunos archivos que contiene no se pudieron actualizar. Contacta a soporte para corregirlos.',
                            8,
                        );
                    } else {
                        message.success('Nombre de la carpeta actualizado correctamente.');
                    }
                } else {
                    message.error('Error al actualizar el nombre de la carpeta.');
                }
            } catch (error) {
                console.error('[handleEditFolder] Error al actualizar el nombre de la carpeta:', error);
                const status = error?.response?.status;
                if (status === 401) {
                    message.error('Tu sesión expiró o fue cerrada. Vuelve a iniciar sesión para continuar.');
                } else if (status === 404) {
                    message.error('La carpeta ya no existe en esa ruta. Recarga el árbol e inténtalo de nuevo.');
                } else {
                    message.error(error?.response?.data?.error || 'Error al actualizar el nombre de la carpeta.');
                }
            }
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, selectedItem, handleAction, userLogin]
    );

    // Evita el doble envío de handleEditFolder: al hacer click en "Guardar" o
    // "Cancelar", el input dispara onBlur ANTES que el onClick del botón (el
    // navegador quita el foco al presionar mousedown), así que sin esta guarda
    // el rename se enviaba dos veces -la segunda con la ruta ya renombrada- y
    // el backend respondía 404 ("La carpeta ya no existe en esa ruta"),
    // aunque el primer renombrado sí hubiera funcionado.
    const suppressBlurSaveRef = useRef(false);

    const suppressBlurSave = useCallback(() => {
        suppressBlurSaveRef.current = true;
    }, []);

    const handleFolderNameCommit = useCallback(() => {
        if (newName.trim()) {
            handleEditFolder(selectedItem.path, newName.trim());
            toggleEditMode();
        } else {
            message.error('El nombre no puede estar vacío.');
            cancelEditMode();
        }
    }, [newName, selectedItem, handleEditFolder, toggleEditMode, cancelEditMode]);

    const handleFolderNameBlur = useCallback(() => {
        if (suppressBlurSaveRef.current) {
            suppressBlurSaveRef.current = false;
            return;
        }
        handleFolderNameCommit();
    }, [handleFolderNameCommit]);

    // ─────────────────────────────────────────────────────────────────────────
    // CREAR CARPETA
    // ─────────────────────────────────────────────────────────────────────────
    const handleCreateFolder = useCallback(async () => {
        const nodeRaiz = 'DocumentosPOA';
        if (!newFolderName.trim()) {
            message.warning('Debes ingresar un nombre para la carpeta.');
            return;
        }
        if (!FOLDER_NAME_REGEX.test(newFolderName)) {
            message.error('El nombre contiene caracteres no permitidos.');
            return;
        }
        if (!selectedItem) {
            message.warning('No hay una carpeta seleccionada.');
            return;
        }
        const tieneActividad = !!codActividadParametro;
        const tieneProyectoEDAH = !!proyectoRutaNew;
        let tmpRuta = '';
        let fatherResource = '';
        try {
            if (tieneActividad) {
                const parentPath = selectedItem.path || '';
                tmpRuta = `${codActividadParametro}/${parentPath}`.replace(/\/{2,}/g, '/');
                fatherResource = `${nodeRaiz}/${tmpRuta}`;
            } else if (tieneProyectoEDAH) {
                const cleanP = proyectoRutaNew.replace(/\/$/, '').trim();
                if (selectedItem.path === cleanP || !selectedItem.path) {
                    tmpRuta = `${rutaInformacionGeneral}/${cleanP}`;
                    fatherResource = `${nodeRaiz}/${tmpRuta}`;
                } else {
                    tmpRuta = `${rutaInformacionGeneral}/${cleanP}/${selectedItem.path}`.replace(/\/{2,}/g, '/');
                    fatherResource = `${nodeRaiz}/${tmpRuta}`;
                }
            } else {
                message.warning('No se pudo determinar el contexto.');
                return;
            }
            const check = await checkFolderName(tmpRuta, newFolderName, nodeRaiz);
            if (check.data.status === 'error') {
                message.error(check.data.message);
                return;
            }
            const resourceInfo = await handleAction({
                user: userLogin,
                actionType: 'createFolder',
                nameServer: newFolderName,
                resourceDetails: {
                    nameResource: newFolderName,
                    Path: tmpRuta,
                    fatherResource,
                    typeResource: 'folder',
                    key: `${tmpRuta}/${newFolderName}`.replace(/\/{2,}/g, '/'),
                    fullPath: `${nodeRaiz}/${tmpRuta}/${newFolderName}`.replace(/\/{2,}/g, '/'),
                    resourceType: tieneProyectoEDAH ? `Folder ${rutaInformacionGeneral}` : 'Folder POA',
                },
            });
            await createFolder(tmpRuta, newFolderName, nodeRaiz, resourceInfo);
            message.success('Carpeta creada exitosamente');
            const parentPath = selectedItem.path || '';
            setTreeData((prev) => addNodeToTree(prev, parentPath, newFolderName));
            setSelectedItem((prev) => {
                if (!prev || prev.path !== parentPath) return prev;
                const newNode = {
                    name: newFolderName,
                    key: `${parentPath}/${newFolderName}`,
                    path: `${parentPath}/${newFolderName}`,
                    type: 'folder',
                    isLeaf: false,
                    children: [],
                    viewName: newFolderName,
                    canDelete: true,
                };
                return { ...prev, children: [...(prev.children ?? []), newNode], pathFromClient: prev.path };
            });
            setIsModalVisible(false);
            setNewFolderName('');
        } catch {
            message.error('Ocurrió un error al crear la carpeta.');
        }
    }, [newFolderName, selectedItem, codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, handleAction, userLogin]);

    // ─────────────────────────────────────────────────────────────────────────
    // SUBIDA DE ARCHIVOS (único archivo)
    // ─────────────────────────────────────────────────────────────────────────
    const handleCustomRequest = useCallback(
        async ({ file, onSuccess, onError, resourceType, categoriaUser }) => {
            try {
                const nodeRaiz = 'DocumentosPOA';
                let tmpRuta = '';
                let fullPath = '';
                let fatherResource = '';

                if (esInformeEDAH) {
                    const cleanP = (proyectoRutaNew || '').replace(/\/$/, '').trim();
                    if (!cleanP) {
                        message.error('Carpeta de destino no detectada. Selecciona una carpeta antes de subir.');
                        return onError?.(new Error('proyectoRutaNew vacío'));
                    }
                    tmpRuta = `${rutaInformacionGeneral}/${cleanP}`;
                    fullPath = `${nodeRaiz}/${tmpRuta}/${file.name}`;
                    fatherResource = `${nodeRaiz}/${tmpRuta}`;
                } else if (codActividadParametro) {
                    const clientPath = selectedItem?.pathFromClient;
                    if (!clientPath || clientPath === 'undefined') {
                        message.error('Ruta de destino inválida. Por favor cierra y vuelve a abrir el gestor de archivos.');
                        return onError?.(new Error('pathFromClient inválido'));
                    }
                    tmpRuta = `${codActividadParametro}/${clientPath}`;
                    fullPath = `${nodeRaiz}/${tmpRuta}/${file.name}`;
                    fatherResource = `${nodeRaiz}/${codActividadParametro}/${clientPath}`;
                }

                if (!tmpRuta || tmpRuta.includes('undefined')) {
                    message.error('Ruta de destino inválida. Por favor cierra y vuelve a abrir el gestor de archivos.');
                    return onError?.(new Error('Ruta inválida'));
                }

                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'uploadFile',
                    nameServer: file.name,
                    resourceDetails: {
                        nameResource: file.name,
                        Path: tmpRuta,
                        fatherResource,
                        typeResource: 'file',
                        key: `${selectedItem.pathFromClient}/${file.name}`,
                        fullPath,
                        resourceType,
                    },
                });

                if (sinFechaFile) resourceInfo.actionData.fechaAction = null;

                const fd = new FormData();
                fd.append('pathFromClient', `${nodeRaiz}/${tmpRuta}`);
                fd.append('file', file);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));
                fd.append('categoriaUser', categoriaUser);
                const res = await axios.post(`${baseUrl}/api/upload`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                if (res.data.status === 'success') {
                    const { viewName: realName, serverFileName, ...rest } = res.data.data;
                    const uid = generateRandomUid();
                    const newFileNode = {
                        name: realName,
                        key: `${selectedItem.pathFromClient}/${serverFileName}-${uid}`,
                        type: 'file',
                        level: selectedItem.level + 1,
                        path: `${selectedItem.pathFromClient}/${serverFileName}`,
                        pathFromClient: selectedItem.pathFromClient,
                        uid,
                        serverFileName,
                        isLeaf: true,
                        viewName: realName,
                        typeS: 'Soporte',
                        resourceType,
                        metadato: { ...rest, viewName: realName, serverFileName, detailJSON: JSON.stringify(resourceInfo) },
                    };
                    setTreeData((prev) =>
                        addFileToTree(prev, selectedItem.pathFromClient, newFileNode)
                    );
                    setSelectedItem((prev) =>
                        prev?.pathFromClient === selectedItem.pathFromClient
                            ? { ...prev, children: [...(prev.children ?? []), newFileNode] }
                            : prev
                    );
                    message.success(`Archivo "${realName}" subido correctamente`);
                    onUploaded?.();
                    onSuccess?.('ok');
                } else {
                    throw new Error('Error en upload');
                }
            } catch (error) {
                console.error('Upload error:', error);
                message.error(`Error al subir "${file.name}"`);
                onError?.(error);
            }
        },
        [codActividadParametro, esInformeEDAH, proyectoRutaNew, rutaInformacionGeneral, selectedItem, handleAction, userLogin, sinFechaFile]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // SUBIDA MÚLTIPLE DE ARCHIVOS
    // ─────────────────────────────────────────────────────────────────────────
    const handleUploadMultipleFileAll = useCallback(
        async (file, resourceType) => {
            try {
                const nodeRaiz = 'DocumentosPOA';
                let tmpRuta = '';
                let fullPath = '';
                let fatherResource = '';
                let tipoRecurso = resourceType;

                if (esInformeEDAH) {
                    const cleanP = (proyectoRutaNew || '').replace(/\/$/, '').trim();
                    if (!cleanP) {
                        return { success: false, fileName: file.name, error: 'Carpeta de destino no detectada.' };
                    }
                    tmpRuta = `${rutaInformacionGeneral}/${cleanP}`;
                    fullPath = `${nodeRaiz}/${tmpRuta}/${file.name}`;
                    fatherResource = `${nodeRaiz}/${tmpRuta}`;
                    tipoRecurso = `Soporte ${rutaInformacionGeneral}`;
                } else {
                    const clientPath = selectedItem?.pathFromClient;
                    if (!clientPath || clientPath === 'undefined') {
                        return { success: false, fileName: file.name, error: 'Ruta de destino inválida.' };
                    }
                    tmpRuta = `${codActividadParametro}/${clientPath}`;
                    fullPath = `${nodeRaiz}/${tmpRuta}/${file.name}`;
                    fatherResource = `${nodeRaiz}/${codActividadParametro}/${clientPath}`;
                }

                if (!tmpRuta || tmpRuta.includes('undefined')) {
                    return { success: false, fileName: file.name, error: 'Ruta de destino inválida.' };
                }

                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'uploadFile',
                    nameServer: file.name,
                    resourceDetails: {
                        nameResource: file.name,
                        Path: tmpRuta,
                        fatherResource,
                        typeResource: 'file',
                        key: `${selectedItem?.pathFromClient}/${file.name}`,
                        fullPath,
                        resourceType: tipoRecurso,
                    },
                });

                if (sinFechaMultiple) resourceInfo.actionData.fechaAction = null;

                const fd = new FormData();
                fd.append('pathFromClient', `${nodeRaiz}/${tmpRuta}`);
                fd.append('file', file);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));
                const res = await axios.post(`${baseUrl}/api/upload`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });

                if (res.data.status === 'success') {
                    const { viewName: realName, serverFileName, ...rest } = res.data.data;
                    const uid = generateRandomUid();
                    const newFileNode = {
                        name: realName,
                        key: `${selectedItem.pathFromClient}/${serverFileName}-${uid}`,
                        type: 'file',
                        level: selectedItem.level + 1,
                        path: `${selectedItem.pathFromClient}/${serverFileName}`,
                        pathFromClient: selectedItem.pathFromClient,
                        uid,
                        serverFileName,
                        isLeaf: true,
                        viewName: realName,
                        typeS: esInformeEDAH ? rutaInformacionGeneral : 'Soporte',
                        resourceType: tipoRecurso,
                        metadato: { ...rest, viewName: realName, serverFileName, detailJSON: JSON.stringify(resourceInfo) },
                    };
                    setTreeData((prev) =>
                        addFileToTree(prev, selectedItem.pathFromClient, newFileNode)
                    );
                    setSelectedItem((prev) =>
                        prev?.pathFromClient === selectedItem.pathFromClient
                            ? { ...prev, children: [...(prev.children ?? []), newFileNode] }
                            : prev
                    );
                    onUploaded?.();
                    return { success: true, fileName: realName };
                } else {
                    throw new Error(`Error al subir: ${file.name}`);
                }
            } catch (error) {
                console.error('Upload multiple error:', error);
                const backendMessage = error?.response?.data?.error || error?.message;
                return { success: false, fileName: file.name, error: backendMessage };
            }
        },
        [codActividadParametro, esInformeEDAH, proyectoRutaNew, rutaInformacionGeneral, selectedItem, handleAction, userLogin, sinFechaMultiple]
    );

    const handleFormSubmitAllFile = useCallback(async () => {
        if (!fileListAllFile?.length) {
            message.error('Por favor, cargue al menos un archivo.');
            return;
        }
        try {
            const values = !esInformeEDAH ? await formAllFile.validateFields() : {};
            const resourceType = esInformeEDAH ? `Soporte ${rutaInformacionGeneral}` : values.resourceType;
            setIsSubmittingAllFile(true);
            const validFiles = fileListAllFile
                .filter((fw) => fw.originFileObj)
                .map((fw) => fw.originFileObj);
            if (!validFiles.length) {
                message.error('No se encontraron archivos válidos para subir.');
                return;
            }
            setUploadProgressAllFile({ current: 0, total: validFiles.length });
            const resultados = [];
            for (const file of validFiles) {
                const resultado = await handleUploadMultipleFileAll(
                    new File([file], decodeURIComponent(file.name), { type: file.type }),
                    resourceType
                );
                resultados.push(resultado ?? { success: false, fileName: file.name, error: 'Error desconocido' });
                setUploadProgressAllFile((prev) => ({ ...prev, current: prev.current + 1 }));
            }

            const exitosos = resultados.filter((r) => r.success);
            const fallidos = resultados.filter((r) => !r.success);

            setFileListAllFile([]);
            formAllFile.resetFields();
            setSinFechaMultiple(false);
            setIsModalVisibleAllFile(false);

            if (fallidos.length === 0) {
                message.success(`${exitosos.length} archivo(s) cargado(s) correctamente.`);
            } else {
                const detalleFallos = (
                    <ul style={{ maxHeight: 240, overflow: 'auto', paddingLeft: 20, margin: 0 }}>
                        {fallidos.map((f, i) => (
                            <li key={i}><strong>{f.fileName}</strong>{f.error ? `: ${f.error}` : ''}</li>
                        ))}
                    </ul>
                );
                if (exitosos.length === 0) {
                    message.error(`No se pudo subir ningún archivo (${fallidos.length}).`);
                    Modal.error({ title: 'Archivos que no se pudieron subir', content: detalleFallos });
                } else {
                    message.warning(`${exitosos.length} de ${resultados.length} archivo(s) subidos. ${fallidos.length} con error.`);
                    Modal.warning({ title: `${fallidos.length} archivo(s) con error`, content: detalleFallos });
                }
            }
        } catch (err) {
            console.error('Error al subir archivos:', err);
            message.error('Error al subir los archivos.');
        } finally {
            setIsSubmittingAllFile(false);
            setUploadProgressAllFile({ current: 0, total: 0 });
        }
    }, [fileListAllFile, esInformeEDAH, rutaInformacionGeneral, formAllFile, handleUploadMultipleFileAll]);

    // Estado local del spinner de submit del modal multiple (necesario para el finally)
    const [isSubmittingAllFile, setIsSubmittingAllFile] = useState(false);
    const [uploadProgressAllFile, setUploadProgressAllFile] = useState({ current: 0, total: 0 });

    // ─────────────────────────────────────────────────────────────────────────
    // SUBMIT FORMULARIO ÚNICO (handleFormSubmit)
    // ─────────────────────────────────────────────────────────────────────────
    const handleFormSubmit = useCallback(async () => {
        try {
            await form.validateFields();
            setIsSubmitting(true);
            const tituloForm = form.getFieldValue('titulo');
            const resourceType = form.getFieldValue('resourceType');
            const titulo = tituloForm || editRecord?.nameResource;
            let categoriaUser = form.getFieldValue('categoriaUser');
            if (!categoriaUser || categoriaUser === 'undefined') categoriaUser = 'Sin categorizar';

            if (fileToUpload) {
                const ext = fileToUpload.name.split('.').pop();
                const renamed = new File([fileToUpload], decodeURIComponent(`${titulo}.${ext}`), {
                    type: fileToUpload.type,
                });
                handleCustomRequest({
                    file: renamed,
                    onSuccess: () => {
                        setFileList([]);
                        setFileToUpload(null);
                        setIsModalFileVisible(false);
                        setIsSubmitting(false);
                    },
                    onError: () => setIsSubmitting(false),
                    resourceType,
                    categoriaUser,
                });
            } else if (editRecord) {
                const newDetailJSON = {
                    ...(JSON.parse(editRecord.detailJSON || '{}')),
                    nameResource: titulo,
                    resourceType,
                };
                const res = await handleAction({
                    user: userLogin,
                    actionType: 'updateFileMetadata',
                    resourceDetails: {
                        oid: editRecord.oid,
                        nameResource: titulo,
                        resourceType,
                        detailJSON: JSON.stringify(newDetailJSON),
                    },
                });
                if (res.status === 'success') {
                    message.success('Soporte actualizado correctamente.');
                    setIsModalFileVisible(false);
                    setFileToUpload(null);
                    setIsSubmitting(false);
                } else {
                    throw new Error('No se pudo actualizar.');
                }
            }
        } catch {
            setIsSubmitting(false);
        }
    }, [form, editRecord, fileToUpload, handleCustomRequest, handleAction, userLogin]);

    const handleOkFile = useCallback(() => {
        setIsModalFileVisible(true);
        form.resetFields();
    }, [form]);

    const handleCancelFile = useCallback(() => {
        form.resetFields();
        setIsModalFileVisible(false);
        setEditRecord(null);
        setFileToUpload(null);
        setFileList([]);
        setSinFechaFile(false);
    }, [form]);

    const showModalAllFile = useCallback(() => setIsModalVisibleAllFile(true), []);
    const handleCancelAllFile = useCallback(() => {
        setIsModalVisibleAllFile(false);
        setFileListAllFile([]);
        formAllFile.resetFields();
        setSinFechaMultiple(false);
    }, [formAllFile]);

    // ─────────────────────────────────────────────────────────────────────────
    // ELIMINACIÓN DE ARCHIVOS
    // ─────────────────────────────────────────────────────────────────────────
    const handleDeleteFile = useCallback(
        async (file) => {
            try {
                const nodeRaiz = 'DocumentosPOA';
                const tieneActividad = !!codActividadParametro;
                const tieneProyectoEDAH = !!proyectoRutaNew;
                let tmpFilepathserver = '';
                let tmpFull = '';
                let context = '';
                let fatherResource = '';

                if (tieneActividad) {
                    context = 'poa';
                    let subPath = file.pathFromClient && file.pathFromClient !== 'undefined'
                        ? file.pathFromClient
                        : (file.path?.replace(/^DocumentosPOA[\\/]/, '').trim() ?? '');
                    if (subPath?.endsWith(file.serverFileName)) {
                        subPath = subPath.replace(file.serverFileName, '').replace(/\/$/, '');
                    }
                    tmpFull = subPath
                        ? `${codActividadParametro}/${subPath}/${file.serverFileName}`
                        : `${codActividadParametro}/${file.serverFileName}`;
                    tmpFilepathserver = limpiarCodigoArchivoEnRuta(tmpFull);
                    fatherResource = selectedItem?.name || '';
                } else if (tieneProyectoEDAH) {
                    context = rutaInformacionGeneral;
                    tmpFull = `/${rutaInformacionGeneral}/${proyectoRutaNew}/${file.serverFileName}`;
                    tmpFilepathserver = encodeURIComponent(tmpFull);
                    fatherResource = proyectoRutaNew;
                } else {
                    message.warning('No se pudo determinar el tipo de archivo.');
                    return;
                }
                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'deleteFile',
                    nameServer: file.serverFileName,
                    resourceDetails: {
                        nameResource: file.serverFileName,
                        Path: `${nodeRaiz}/${tmpFilepathserver}`,
                        fatherResource,
                        typeResource: 'file',
                        key: file.path || tmpFull,
                        fullPath: `${nodeRaiz}/${tmpFull}`,
                        context,
                    },
                });
                const fd = new FormData();
                fd.append('filePath', tmpFilepathserver);
                fd.append('nodeRaiz', nodeRaiz);
                if (file.metadato?.oid) fd.append('oid', file.metadato.oid);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));
                const res = await axios.post(`${baseUrl}/api/delete-file`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (res.data.status !== 'success') {
                    console.warn('El servidor no eliminó el archivo.');
                    return;
                }
                message.success('Archivo eliminado correctamente.');
                onUploaded?.();
                const filePathNode = file.path
                    ? file.path.substring(0, file.path.lastIndexOf('/') + 1) + file.serverFileName
                    : `${proyectoRutaNew}/${file.serverFileName}`;
                const { updatedTreeData, parentKey } = deleteNodeFromTree(treeData, filePathNode);
                setTreeData(updatedTreeData);
                // Mantiene la selección en el padre, no salta al inicio
                setSelectedItem((prev) => {
                    if (!prev) return prev;
                    const updatedChildren = (prev.children ?? []).filter((c) => c.uid !== file.uid);
                    return { ...prev, children: updatedChildren };
                });
                if (parentKey) {
                    const parentNode = findNodeByKey(updatedTreeData, parentKey);
                    if (parentNode) setSelectedItem(parentNode);
                }
                setTableParams((prev) => ({
                    ...prev,
                    pagination: {
                        ...prev.pagination,
                        total: (selectedItem?.children?.length ?? 1) - 1,
                        current: 1,
                    },
                }));
            } catch (error) {
                console.error('Error en handleDeleteFile:', error);
                message.error('Error inesperado al eliminar el archivo.');
            }
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, selectedItem, treeData, handleAction, userLogin]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // RENOMBRAR ARCHIVO (conserva el código con el que el servidor identifica
    // el soporte, ej. el prefijo numérico "1740502951164-": ese prefijo nunca
    // se toca, solo se reemplaza la parte legible del nombre)
    // ─────────────────────────────────────────────────────────────────────────
    const handleRenameFile = useCallback(
        async (file, nuevoNombreSinExtension) => {
            try {
                const nodeRaiz = 'DocumentosPOA';
                const tieneActividad = !!codActividadParametro;
                const tieneProyectoEDAH = !!proyectoRutaNew;

                const extMatch = file.serverFileName.match(/(\.[a-zA-Z0-9]+)$/);
                const extension = extMatch ? extMatch[0] : '';
                const prefixMatch = file.serverFileName.match(/^(\d{13,}-)/);
                const prefix = prefixMatch ? prefixMatch[1] : '';
                const nuevoNombreLimpio = `${nuevoNombreSinExtension}${extension}`;
                const nuevoServerFileName = `${prefix}${nuevoNombreSinExtension}${extension}`;

                let dirPart = '';
                let fatherResource = '';
                let context = '';

                if (tieneActividad) {
                    context = 'poa';
                    let subPath = file.pathFromClient && file.pathFromClient !== 'undefined'
                        ? file.pathFromClient
                        : (file.path?.replace(/^DocumentosPOA[\\/]/, '').trim() ?? '');
                    if (subPath?.endsWith(file.serverFileName)) {
                        subPath = subPath.replace(file.serverFileName, '').replace(/\/$/, '');
                    }
                    dirPart = subPath ? `${codActividadParametro}/${subPath}` : codActividadParametro;
                    fatherResource = selectedItem?.name || '';
                } else if (tieneProyectoEDAH) {
                    context = rutaInformacionGeneral;
                    dirPart = `/${rutaInformacionGeneral}/${proyectoRutaNew}`;
                    fatherResource = proyectoRutaNew;
                } else {
                    message.warning('No se pudo determinar el tipo de archivo.');
                    return;
                }

                const oldFull = `${dirPart}/${file.serverFileName}`;
                const newFull = `${dirPart}/${nuevoServerFileName}`;
                const dbFullPath = limpiarCodigoArchivoEnRuta(newFull);

                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'renameFile',
                    nameServer: nuevoServerFileName,
                    resourceDetails: {
                        nameResource: nuevoNombreLimpio,
                        Path: `${nodeRaiz}/${dbFullPath}`,
                        fatherResource,
                        typeResource: 'file',
                        key: newFull,
                        fullPath: `${nodeRaiz}/${dbFullPath}`,
                        context,
                    },
                });

                const fd = new FormData();
                fd.append('oldFullPath', `${nodeRaiz}/${oldFull}`);
                fd.append('nuevoServerFileName', nuevoServerFileName);
                if (file.metadato?.oid) fd.append('oid', file.metadato.oid);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));

                const res = await axios.post(`${baseUrl}/api/rename-file`, fd, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                });
                if (res.data.status !== 'success') {
                    message.error(res.data.message || 'No se pudo renombrar el archivo.');
                    return;
                }
                message.success('Nombre del soporte actualizado correctamente.');

                const newTreePath = file.path
                    ? file.path.substring(0, file.path.lastIndexOf('/') + 1) + nuevoServerFileName
                    : file.path;

                const renamer = (nodes) => nodes.map((node) => {
                    if (node.uid === file.uid) {
                        return {
                            ...node,
                            name: nuevoNombreLimpio,
                            serverFileName: nuevoServerFileName,
                            viewName: nuevoNombreLimpio,
                            path: newTreePath,
                            metadato: node.metadato ? { ...node.metadato, nameResource: nuevoNombreLimpio, fullPath: `${nodeRaiz}/${dbFullPath}` } : node.metadato,
                        };
                    }
                    if (node.children) return { ...node, children: renamer(node.children) };
                    return node;
                });
                setTreeData((prev) => renamer(prev));
                setSelectedItem((prev) => (prev ? { ...prev, children: renamer(prev.children ?? []) } : prev));
            } catch (error) {
                console.error('[handleRenameFile] Error al renombrar el archivo:', error);
                const status = error?.response?.status;
                if (status === 404) {
                    message.error('El archivo ya no existe en esa ruta. Recarga el árbol e inténtalo de nuevo.');
                } else if (status === 409) {
                    message.error('Ya existe un archivo con ese nombre en esta carpeta.');
                } else {
                    message.error(error?.response?.data?.message || 'Error al renombrar el archivo.');
                }
            }
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, selectedItem, handleAction, userLogin]
    );

    const openRenameFileModal = useCallback((record) => {
        const ext = (record.serverFileName.match(/(\.[a-zA-Z0-9]+)$/) || [''])[0];
        const cleaned = cleanFileName(record.name || record.serverFileName);
        const nameWithoutExt = ext && cleaned.endsWith(ext) ? cleaned.slice(0, -ext.length) : cleaned;
        setRenameFileTarget(record);
        setRenameFileValue(nameWithoutExt);
        setRenameFileExtension(ext);
        setRenameFileModalVisible(true);
    }, []);

    const handleRenameFileSubmit = useCallback(async () => {
        const value = renameFileValue.trim();
        if (!value) {
            message.error('El nombre no puede estar vacío.');
            return;
        }
        if (!FOLDER_NAME_REGEX.test(value)) {
            message.error('El nombre contiene caracteres no permitidos.');
            return;
        }
        setIsRenamingFile(true);
        await handleRenameFile(renameFileTarget, value);
        setIsRenamingFile(false);
        setRenameFileModalVisible(false);
    }, [renameFileValue, renameFileTarget, handleRenameFile]);

    // ─────────────────────────────────────────────────────────────────────────
    // ELIMINACIÓN DE CARPETA
    // ─────────────────────────────────────────────────────────────────────────
    const handleDeleteFolder = useCallback(
        async (path, data) => {
            const nodeRaiz = 'DocumentosPOA';
            const tieneActividad = !!codActividadParametro;
            const tieneProyectoEDAH = !!proyectoRutaNew;
            let tmpRuta = '';
            let fatherResource = '';
            const nameServer =
                data.serverFileName?.trim() ? data.serverFileName : data.name;
            try {
                if (tieneActividad) {
                    tmpRuta = `${codActividadParametro}/${path}`.replace(/\/{2,}/g, '/');
                    fatherResource = `${nodeRaiz}/${codActividadParametro}`;
                } else if (tieneProyectoEDAH) {
                    const cleanP = proyectoRutaNew.replace(/\/$/, '').trim();
                    if (path === cleanP || !path.includes('/')) {
                        tmpRuta = `${rutaInformacionGeneral}/${cleanP}`;
                        fatherResource = rutaInformacionGeneral;
                    } else {
                        tmpRuta = `${rutaInformacionGeneral}/${cleanP}/${path}`.replace(/\/{2,}/g, '/');
                        fatherResource = tmpRuta.substring(0, tmpRuta.lastIndexOf('/'));
                    }
                } else {
                    message.warning('No se pudo determinar el contexto.');
                    return;
                }
                const resourceInfo = await handleAction({
                    user: userLogin,
                    actionType: 'deleteFolder',
                    nameServer,
                    resourceDetails: {
                        nameResource: nameServer,
                        Path: tmpRuta,
                        fatherResource: `${nodeRaiz}/${fatherResource}`.replace(/\/{2,}/g, '/'),
                        typeResource: 'folder',
                        key: data.path,
                        fullPath: `${nodeRaiz}/${tmpRuta}`.replace(/\/{2,}/g, '/'),
                        resourceType: tieneProyectoEDAH ? 'Folder EDAH' : 'Folder POA',
                    },
                });
                const fd = new FormData();
                fd.append('dirPath', tmpRuta);
                fd.append('nodeRaiz', nodeRaiz);
                fd.append('resourceInfo', JSON.stringify(resourceInfo));
                const res = await axios.post(`${baseUrl}/api/delete-directory`, fd);
                if (res.status === 200) {
                    const { updatedTreeData, parentKey } = deleteNodeFromTree(treeData, path);
                    setTreeData(updatedTreeData);
                    // Mantiene selección en el padre
                    if (parentKey) {
                        const parentNode = findNodeByKey(updatedTreeData, parentKey);
                        if (parentNode) setSelectedItem(parentNode);
                    } else {
                        setSelectedItem((prev) =>
                            prev ? { ...prev, children: prev.children?.filter((c) => c.path !== path) } : prev
                        );
                    }
                    setTableParams((prev) => ({
                        ...prev,
                        pagination: { ...prev.pagination, current: 1 },
                    }));
                    message.success('Directorio eliminado correctamente.');
                } else {
                    message.error('Error al eliminar el directorio.');
                }
            } catch {
                message.error('Error al eliminar el directorio.');
            }
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, treeData, handleAction, userLogin]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // CHECKLIST / VALIDACIÓN DE ESTADO / CATEGORÍA
    // ─────────────────────────────────────────────────────────────────────────
    const handleChecklistChange = useCallback(
        async (checked, record) => {
            try {
                const nodeRaiz = 'DocumentosPOA';
                const tmpcodAct = limpiarCodigo(codigo_tarea.codigo_tarea.codActividad);
                const codActParam = nodeRaiz + '/actividad_' + tmpcodAct + `/${record.path}`;
                const fullPathLimpio = limpiarCodigoArchivoEnRuta(codActParam);
                const cleanName = record.name.replace(/^\d+-/, '');
                let payload;
                if (record.metadato) {
                    payload = {
                        oid: record.metadato.oid,
                        nameResource: cleanName,
                        fullPath: fullPathLimpio,
                        user: record.metadato.user || 'admin',
                        state: 1,
                        typeAction: record.metadato.typeAction,
                        dateTime: record.metadato.dateTime,
                        detailJSON: record.metadato.detailJSON,
                        validado: checked ? 2 : 1,
                        validado_user: userLogin,
                        fecha_validacion: checked ? new Date().toISOString().slice(0, 10) : null,
                    };
                } else {
                    const resourceInfo = await handleAction({
                        user: 'admin',
                        actionType: 'uploadFile',
                        nameServer: cleanName,
                        resourceDetails: {
                            nameResource: record.name,
                            Path: fullPathLimpio,
                            fatherResource: selectedItem?.name,
                            typeResource: 'file',
                            key: `${selectedItem?.pathFromClient}/${record.name}`,
                            fullPath: fullPathLimpio,
                            resourceType: nameFileAll,
                        },
                    });
                    payload = {
                        nameResource: cleanName,
                        fullPath: fullPathLimpio,
                        user: 'admin',
                        state: 1,
                        typeAction: 'uploadFile',
                        dateTime: new Date().toISOString(),
                        detailJSON: JSON.stringify(resourceInfo),
                        validado: checked ? 2 : 1,
                        validado_user: userLogin,
                        fecha_validacion: checked ? new Date().toISOString().slice(0, 10) : null,
                    };
                }
                const res = await enviarHistorico(payload);
                if (res) {
                    checked
                        ? message.success('El documento fue validado correctamente')
                        : message.info('Desmarcación del documento realizada correctamente');

                    const updatedMeta = (n) => ({
                        ...n,
                        metadato: {
                            ...(n.metadato || payload),
                            validado: checked ? 2 : 1,
                            validado_user: userLogin,
                            oid: res.oid ?? n.metadato?.oid,
                        },
                    });

                    // ✅ Actualiza el árbol
                    setTreeData((prev) => updateNodeMetadata(prev, record.key, updatedMeta));

                    // ✅ Actualiza selectedItem.children para que la tabla re-renderice
                    // sin esperar a que el usuario cambie de nodo en el árbol
                    setSelectedItem((prev) => {
                        if (!prev?.children) return prev;
                        return {
                            ...prev,
                            children: prev.children.map((child) =>
                                child.key === record.key ? updatedMeta(child) : child
                            ),
                        };
                    });
                }
            } catch (error) {
                console.error('Error al marcar checklist:', error);
            }
        },
        [codigo_tarea, selectedItem, nameFileAll, handleAction, userLogin]
    );

    const handleCategoriaChange = useCallback(
        async (key, record) => {
            if (record.metadato?.estadovalidacion === 'Validado') {
                message.warning('No puedes modificar la categoría porque el archivo ya está validado.');
                return;
            }
            const id = record.metadato?.oid;
            const result = await ActualizarTipoEntrega(id, key || 'Sin categorizar');
            if (result.success) {
                message.success('Tipo de categoría actualizado correctamente');
                const updatedMeta = (n) => ({
                    ...n,
                    metadato: { ...(n.metadato || {}), estado: key },
                });

                // ✅ Actualiza el árbol
                setTreeData((prev) => updateNodeMetadata(prev, record.key, updatedMeta));

                // ✅ Actualiza selectedItem.children para que la tabla re-renderice
                // sin esperar a que el usuario cambie de nodo en el árbol
                setSelectedItem((prev) => {
                    if (!prev?.children) return prev;
                    return {
                        ...prev,
                        children: prev.children.map((child) =>
                            child.key === record.key ? updatedMeta(child) : child
                        ),
                    };
                });
            } else {
                message.error(result.message);
            }
        },
        []
    );

    const handleToggleValidacion = useCallback(
        async (record) => {
            if (!permissionUpdate5) return;
            const id = record.metadato?.oid;
            const actual = record.metadato?.estadovalidacion || 'Sin validar';
            const nuevo = actual === 'Validado' ? 'Sin validar' : 'Validado';
            const result = await ValidarEstadoActa(id, nuevo);
            if (result.success) {
                message.success('Estado de validación actualizado correctamente');
                setTreeData((prev) =>
                    updateNodeMetadata(prev, record.key, (n) => ({
                        ...n,
                        metadato: { ...(n.metadato || {}), estadovalidacion: nuevo },
                    }))
                );
            } else {
                message.error(result.message);
            }
        },
        [permissionUpdate5]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // DESCARGA
    // ─────────────────────────────────────────────────────────────────────────
    const handleDescargar = useCallback(
        (node) => {
            const nodeRaiz = 'DocumentosPOA';
            const tieneActividad = !!codActividadParametro;
            const tieneProyectoEDAH = !!proyectoRutaNew;
            let tmpRuta = '';
            if (tieneActividad) {
                tmpRuta = `${codActividadParametro}/${node.path}`.replace(/\/{2,}/g, '/');
            } else if (tieneProyectoEDAH) {
                const cleanP = (proyectoRutaNew || '').replace(/\/$/, '').trim();
                let inner = node.path?.replace(/\\/g, '/').replace(/^\/+/, '').trim() ?? '';
                if (inner.startsWith(`${cleanP}/`)) inner = inner.replace(`${cleanP}/`, '');
                tmpRuta =
                    node.level === 0 || node.path === cleanP
                        ? `${rutaInformacionGeneral}/${cleanP}`
                        : `${rutaInformacionGeneral}/${cleanP}/${inner}`;
                tmpRuta = tmpRuta.replace(/\/{2,}/g, '/').replace(/\/$/, '');
            }
            if (!tmpRuta) {
                message.warning('No se pudo determinar la ruta de descarga.');
                return;
            }
            const rutaNorm = tmpRuta
                .normalize('NFC')
                .split('/')
                .map(encodeURIComponent)
                .join('/');
            const fd = new FormData();
            fd.append('ruta', rutaNorm);
            fd.append('nodeRaiz', nodeRaiz);

            startDownload({
                url: `${baseUrl}/api/descargar-archivos`,
                formData: fd,
                key: node.path,
                name: node.viewName,
                type: node.type,
                onSuccess: (blob, headers) => {
                    const url = window.URL.createObjectURL(new Blob([blob]));
                    const a = document.createElement('a');
                    a.href = url;
                    if (node.type === 'folder') {
                        a.download = `${node.viewName}.zip`;
                    } else {
                        const cd = headers['content-disposition'];
                        let filename = node.viewName;
                        if (cd) {
                            const m = cd.match(/filename="?(.+?)"?$/);
                            if (m?.[1]) filename = decodeURIComponent(m[1]);
                        }
                        a.download = filename.replace(/^\d{6,}-/, '');
                    }
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                    window.URL.revokeObjectURL(url);
                },
            });
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral, startDownload]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // VER ARCHIVO EN NUEVA PESTAÑA
    // ─────────────────────────────────────────────────────────────────────────
    const fetchFileUrl = useCallback(
        async (record) => {
            try {
                // Misma construcción de ruta que handleDescargar (reutiliza el
                // endpoint /api/descargar-archivos): la ruta estática de
                // DocumentosFAO solo se navegaba directamente (window.open),
                // nunca por XHR, así que no tiene CORS habilitado para pedirla
                // por axios cuando el frontend corre en un origen distinto al
                // backend (p. ej. en desarrollo local contra el backend remoto).
                const nodeRaiz = 'DocumentosPOA';
                const tieneActividad = !!codActividadParametro;
                const tieneProyectoEDAH = !!proyectoRutaNew;
                let tmpRuta = '';
                if (tieneActividad) {
                    tmpRuta = `${codActividadParametro}/${record.path}`.replace(/\/{2,}/g, '/');
                } else if (tieneProyectoEDAH) {
                    const cleanP = (proyectoRutaNew || '').replace(/\/$/, '').trim();
                    let inner = record.path?.replace(/\\/g, '/').replace(/^\/+/, '').trim() ?? '';
                    if (inner.startsWith(`${cleanP}/`)) inner = inner.replace(`${cleanP}/`, '');
                    tmpRuta =
                        record.level === 0 || record.path === cleanP
                            ? `${rutaInformacionGeneral}/${cleanP}`
                            : `${rutaInformacionGeneral}/${cleanP}/${inner}`;
                    tmpRuta = tmpRuta.replace(/\/{2,}/g, '/').replace(/\/$/, '');
                }
                if (!tmpRuta) {
                    message.warning('No se pudo determinar el tipo de archivo.');
                    return;
                }
                const rutaNorm = tmpRuta
                    .normalize('NFC')
                    .split('/')
                    .map(encodeURIComponent)
                    .join('/');

                const fileName = record.serverFileName || record.viewName || record.name;
                if (isRenderableExtension(fileName)) {
                    // Tipos que el navegador puede mostrar (pdf, imágenes...):
                    // se navega directo a la ruta estática real del archivo,
                    // así la pestaña nueva muestra esa URL en vez de un
                    // blob:... temporal.
                    const fileUrl = `${baseUrl}/DocumentosFAO/${nodeRaiz}/${rutaNorm}`;
                    window.open(fileUrl, '_blank', 'noopener,noreferrer');
                    return;
                }

                // Tipos que el navegador no puede mostrar (xlsx, docx, etc.):
                // se descargan vía el endpoint autenticado y se fuerza la
                // descarga con un <a download>, la única forma confiable de
                // que se use el nombre real en el archivo guardado.
                const fd = new FormData();
                fd.append('ruta', rutaNorm);
                fd.append('nodeRaiz', nodeRaiz);

                const response = await axios.post(`${baseUrl}/api/descargar-archivos`, fd, {
                    responseType: 'blob',
                });
                const mimeType = response.headers['content-type'] || 'application/octet-stream';
                const displayName = processFileName(record.viewName || record.name || record.serverFileName, 1000);
                const blob = new Blob([response.data], { type: mimeType });
                const fileUrl = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = fileUrl;
                a.download = displayName;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                setTimeout(() => window.URL.revokeObjectURL(fileUrl), 30000);
            } catch {
                message.error('Error al intentar abrir el archivo.');
            }
        },
        [codActividadParametro, proyectoRutaNew, rutaInformacionGeneral]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // TITLE RENDERER (árbol)
    // ─────────────────────────────────────────────────────────────────────────
    const titleRenderer = useCallback((node) => {
        const nombre = node.type === 'folder'
            ? node.viewName
            : cleanFileName(decodeURIComponent(node.viewName));
        return (
            <Tooltip title={nombre} placement="right">
                <span
                    style={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}
                    className={node.highlighted ? 'highlightedNode' : ''}
                >
                    {truncateText(nombre, 20)}
                </span>
            </Tooltip>
        );
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // VALIDACIÓN DE ARCHIVO ANTES DE SUBIR
    // ─────────────────────────────────────────────────────────────────────────
    const validateFile = useCallback((file) => {
        if (file.size === 0 || file.type?.includes('directory')) {
            message.warning(`"${file.name}" es una carpeta y no está permitida.`);
            return false;
        }
        if (file.size > MAX_FILE_SIZE) {
            message.warning(`"${file.name}" excede el tamaño máximo de 100MB.`);
            return false;
        }
        return true;
    }, []);

    // ─────────────────────────────────────────────────────────────────────────
    // COLUMNAS DE TABLA (memoizadas para evitar re-renders costosos)
    // ─────────────────────────────────────────────────────────────────────────
    const columnaChecklist = useMemo(() => ({
        title: 'Validado',
        key: 'checklist',
        width: 100,
        align: 'center',
        render: (_, record) => {
            if (record.type !== 'file') return null;
            const validado = Number(record.metadato?.validado);
            return (
                <input
                    type="checkbox"
                    checked={validado === 2}
                    disabled={!permissionUpdate2}
                    onChange={(e) => {
                        if (permissionUpdate2) handleChecklistChange(e.target.checked, record);
                    }}
                />
            );
        },
    }), [permissionUpdate2, handleChecklistChange]);

    const columnaCategoria = useMemo(() => ({
        title: 'Clasificación',
        key: 'estado',
        width: 180,
        align: 'center',
        render: (_, record) => {
            const estadoActual = record.metadato?.estado || 'Sin categorizar';
            const isValidado = record.metadato?.estadovalidacion === 'Validado';
            const bg = estadoActual === 'Sin categorizar' ? '#1890ff2b' : '#52c41a3d';
            const menu = {
                items: OPCIONES_ESTADO.map((e) => ({
                    key: e,
                    label: (
                        <span style={{ fontWeight: e === estadoActual ? 'bold' : 'normal', display: 'block', padding: '4px 12px' }}>
                            {e}
                        </span>
                    ),
                })),
                onClick: ({ key }) => handleCategoriaChange(key, record),
            };
            if (permissionUpdate4 && !isValidado) {
                return (
                    <Dropdown menu={menu} trigger={['click']}>
                        <Button size="small" style={{ color: '#000', backgroundColor: bg, borderColor: bg }}>
                            {estadoActual}
                        </Button>
                    </Dropdown>
                );
            }
            return (
                <span style={{ display: 'inline-block', padding: '4px 12px', backgroundColor: bg, border: `1px solid ${bg}`, borderRadius: 4, color: '#000', opacity: isValidado ? 0.6 : 1 }}>
                    {estadoActual}
                </span>
            );
        },
    }), [permissionUpdate4, handleCategoriaChange]);

    const columnaValidarEstado = useMemo(() => ({
        title: 'Validar estado',
        key: 'estadovalidacion',
        width: 140,
        align: 'center',
        render: (_, record) => {
            if (record.type !== 'file') return null;
            const estadoActual = record.metadato?.estadovalidacion || 'Sin validar';
            const isValidado = estadoActual === 'Validado';
            const bg = !isValidado ? '#1890ff2b' : '#52c41a3d';
            if (permissionUpdate5) {
                return (
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => handleToggleValidacion(record)}
                        style={{ color: '#000', backgroundColor: bg, borderColor: bg }}
                    >
                        {isValidado ? 'Validado' : 'Sin validar'}
                    </Button>
                );
            }
            if (permissionAccess5) {
                return (
                    <Button type="default" size="small" disabled style={{ color: '#000', backgroundColor: bg, borderColor: bg }}>
                        {isValidado ? 'Validado' : 'Sin validar'}
                    </Button>
                );
            }
            return null;
        },
    }), [permissionUpdate5, permissionAccess5, handleToggleValidacion]);

    const columnaCodigoUnico = useMemo(() => ({
        title: 'Código',
        key: 'codUnico',
        width: 100,
        align: 'center',
        render: (_, record) => record.type !== 'file' ? null : (record.metadato?.codUnico || '-'),
    }), []);

    const columnasBase = useMemo(() => [
        {
            title: 'Opciones',
            key: 'action',
            width: 150,
            align: 'left',
            fixed: 'left',
            render: (_, record) => (
                <Space size={4}>
                    {permissionExport && permissionAccess && (
                        <Tooltip title="Descargar">
                            <Popconfirm title="Descargar archivos" description="¿Segur@ que quieres descargar el directorio?" onConfirm={() => handleDescargar(record)} okText="Sí" cancelText="No">
                                <Button type="link" loading={isDownloading(record.path)} icon={!isDownloading(record.path) && <DownloadOutlined style={{ fontSize: '1.1rem' }} />} className="ico_table" />
                            </Popconfirm>
                        </Tooltip>
                    )}
                    {record.type === 'file' && (
                        <>
                            {permissionUpdate3 && permissionAccess3 && (
                                <Tooltip title="Editar nombre del soporte">
                                    <Button type="link" icon={<EditOutlined style={{ fontSize: '1.1rem' }} />} onClick={() => openRenameFileModal(record)} className="ico_table" />
                                </Tooltip>
                            )}
                            {((estadoActEnt && permissionDelete2 && Number(record.metadato?.validado) !== 2) || (!estadoActEnt && permissionDelete && permissionAccess)) && (
                                <Tooltip title="Eliminar archivo">
                                    <Popconfirm title="Eliminación de archivo" description="¿Segur@ que quieres eliminar este archivo?" onConfirm={() => handleDeleteFile(record)} okText="Sí" cancelText="No" icon={<QuestionCircleOutlined style={{ color: 'red' }} />}>
                                        <Button type="link" danger icon={<DeleteOutlined style={{ fontSize: '1.1rem' }} />} className="ico_table" />
                                    </Popconfirm>
                                </Tooltip>
                            )}
                            <Tooltip title="Visualizar el archivo">
                                <Button onClick={() => fetchFileUrl(record)} style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '10px' }}>
                                    <EyeOutlined style={{ fontSize: '24px', color: '#08c' }} />
                                </Button>
                            </Tooltip>
                        </>
                    )}
                    {record.canDelete && permissionDelete3 && permissionAccess3 && (
                        <Tooltip title="Eliminar del directorio">
                            <Popconfirm title="Eliminación de directorio" description="¿Segur@ que quieres eliminar este directorio?" onConfirm={() => handleDeleteFolder(record.path, record)} okText="Sí" cancelText="No" icon={<QuestionCircleOutlined style={{ color: 'red' }} />}>
                                <Button type="link" danger icon={<DeleteOutlined />} className="me-2 p-0" />
                            </Popconfirm>
                        </Tooltip>
                    )}
                </Space>
            ),
        },
        ...(mostrarCheckbox ? [
            ...(permissionAccess4 ? [columnaCategoria] : []),
            ...(permissionAccess5 ? [columnaValidarEstado] : []),
            ...(permissionAccess2 ? [columnaChecklist] : []),
        ] : []),
        ...(isFinalFolder ? [
            ...(codigoTareaTable ? [columnaCodigoUnico] : []),
            {
                title: 'N°',
                key: 'index',
                width: 60,
                align: 'center',
                render: (_, record, index) => record.type === 'file' ? index + 1 : null,
            },
        ] : []),
        {
            title: 'Documento',
            dataIndex: 'name',
            key: 'key',
            width: 400,
            sorter: (a, b) => {
                const getDate = (n) => {
                    const m = n.match(/(\d{2})(\d{2})(\d{4})/);
                    return m ? new Date(`${m[3]}-${m[2]}-${m[1]}`) : new Date(0);
                };
                return getDate(b.viewName) - getDate(a.viewName);
            },
            render: (_, record) => {
                const name = record.type === 'file'
                    ? processFileName(record.viewName, 50)
                    : record.viewName;
                const tooltip = processFileName(record.viewName, 200);
                return (
                    <Tooltip title={tooltip} placement="right">
                        <span>
                            {record.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
                            {` ${name}`}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Formato',
            key: 'extension',
            width: 100,
            align: 'center',
            render: (_, record) => {
                if (typeof record.name !== 'string') return 'Desconocido';
                if (record.type === 'folder') {
                    const pathContainsTarea = record.path?.includes('tarea_221/indicador_34/');
                    if (typeof record.fileCount !== 'undefined') {
                        const count = pathContainsTarea
                            ? (record.fileCount - (record.filePhoto ?? 0) === 0 ? record.fileCount : record.fileCount - (record.filePhoto ?? 0))
                            : record.fileCount;
                        return (
                            <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                <span>Folder </span>
                                <Badge count={count} overflowCount={9999} style={{ backgroundColor: '#52c41a' }} />
                            </Tooltip>
                        );
                    }
                    if (record.children?.some((c) => c.type === 'file')) {
                        return (
                            <Tooltip title="Cantidad de archivos que contiene la carpeta">
                                <span>Folder </span>
                                <Badge count={record.children.length} style={{ backgroundColor: '#52c41a' }} />
                            </Tooltip>
                        );
                    }
                    return 'Folder';
                }
                const parts = record.name.split('.');
                return parts.length > 1 ? parts.pop() : 'Archivo';
            },
        },
        ...(isFinalFolder ? [
            {
                title: 'Tipo de soporte',
                dataIndex: 'name',
                align: 'center',
                key: 'tipoSoporte',
                width: 150,
                render: (_, record) => {
                    try {
                        const detail = JSON.parse(record.metadato?.detailJSON || '{}');
                        return <span>{detail?.resourceData?.resourceType || 'No reportado'}</span>;
                    } catch {
                        return <span>-</span>;
                    }
                },
            },
            ...(mostrarCheckbox ? [{
                title: 'Categoría',
                key: 'categoria',
                align: 'center',
                width: 150,
                render: (_, record) => <span>{record.metadato?.categoriaUser || '-'}</span>,
            }] : []),
            {
                title: 'Usuario',
                dataIndex: 'name',
                align: 'center',
                key: 'usuario',
                width: 150,
                render: (_, record) => <span>{record.metadato?.user || '-'}</span>,
            },
            {
                title: 'Creación',
                dataIndex: 'name',
                key: 'creacion',
                align: 'center',
                width: 150,
                render: (_, record) => {
                    const fecha = record.metadato?.dateTime;
                    return <span>{fecha ? new Date(fecha).toLocaleDateString('sv-SE') : '-'}</span>;
                },
            },
        ] : []),
    ], [
        permissionExport, permissionAccess, permissionUpdate3, permissionAccess3,
        permissionDelete2, permissionDelete, permissionDelete3, permissionAccess4,
        permissionAccess5, permissionAccess2, mostrarCheckbox, isFinalFolder,
        codigoTareaTable, estadoActEnt, columnaCategoria, columnaValidarEstado,
        columnaChecklist, columnaCodigoUnico,
        handleDescargar, openRenameFileModal, handleDeleteFile, fetchFileUrl, handleDeleteFolder,
        isDownloading,
    ]);

    // Árbol mostrado en el Sider: aplica el filtro de coincidencias a las
    // carpetas de Actas/Visitas, dejando el resto del árbol intacto.
    const treeDataFiltrado = useMemo(
        () => aplicarFiltroCoincidenciasEnArbol(treeData, filtroCoincidencias),
        [treeData, filtroCoincidencias]
    );

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER DE ARCHIVOS SELECCIONADOS
    // ─────────────────────────────────────────────────────────────────────────
    const renderSelectedFiles = useCallback(() => {
        if (selectedItem?.type === 'folder') {
            const dataSourceArchivos = mostrarFiltroCoincidencias
                ? aplicarFiltroCoincidencias(selectedItem.children, filtroCoincidencias)
                : selectedItem.children;
            return (
                <>
                    <Table
                        dataSource={dataSourceArchivos}
                        pagination={false}
                        onChange={(p) => setTableParams((prev) => ({ ...prev, pagination: p }))}
                        scroll={{ y: 340 }}
                        columns={columnasBase}
                        rowKey={(r) => r.key}
                    />
                </>
            );
        }
        return (
            <div style={{ textAlign: 'center', padding: '20px' }}>
                <WarningOutlined style={{ color: 'red', marginRight: '8px', fontSize: '2rem' }} />
                <h3>Seleccione el siguiente nivel del árbol para ver los archivos.</h3>
            </div>
        );
    }, [selectedItem, columnasBase, mostrarFiltroCoincidencias, filtroCoincidencias]);

    // ─────────────────────────────────────────────────────────────────────────
    // COMPONENTE DE DRAGGER REUTILIZABLE (único archivo)
    // ─────────────────────────────────────────────────────────────────────────
    const DraggerSingle = useCallback(() => (
        <div className="centered-dragger">
            <Tooltip title="Subir Soporte">
                <Dragger
                    multiple={false}
                    maxCount={1}
                    beforeUpload={() => false}
                    fileList={fileList}
                    onChange={(info) => {
                        const fw = info.fileList[info.fileList.length - 1];
                        const file = fw?.originFileObj;
                        if (!file) { setFileList([]); setFileToUpload(null); return; }
                        if (!validateFile(file)) { setFileList([]); setFileToUpload(null); return; }
                        setFileList([fw]);
                        setFileToUpload(file);
                        form.setFieldValue('soporte', file.name);
                    }}
                    onRemove={() => { setFileList([]); setFileToUpload(null); }}
                    style={{ width: '300px' }}
                >
                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                    <p className="ant-upload-text">Haga clic o arrastre el archivo aquí</p>
                    <p className="ant-upload-hint">Solo se permite un archivo a la vez.</p>
                </Dragger>
            </Tooltip>
        </div>
    ), [fileList, form, validateFile]);

    // ─────────────────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <Drawer
                open={isVisible}
                onClose={handleClose}
                title={
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span className="custom-modal-title" style={{ color: '#FFF' }}>
                            Medios de verificación
                        </span>
                        <Button type="text" onClick={handleClose} icon={<CloseOutlined style={{ fontSize: '20px', color: '#FFF' }} />} />
                    </div>
                }
                placement="right"
                width="100%"
                closable={false}
                destroyOnHidden
                styles={{ body: { padding: 0, height: '100%', overflow: 'hidden' } }}
            >
                <Layout style={{ height: '100vh' }}>
                    {/* ── SIDEBAR ÁRBOL ──────────────────────────────────────── */}
                    <Sider collapsed={isCollapsed} onCollapse={setIsCollapsed} width={350} style={{ background: '#fff', borderRight: '1px solid #f0f0f0' }}>
                        <div style={{ padding: 16 }}>
                            <Button icon={<MenuOutlined />} onClick={() => setIsCollapsed((c) => !c)} block />
                            <div style={{ marginBottom: 5, marginTop: 5 }}>
                                <Input
                                    placeholder="Buscar"
                                    value={searchValue}
                                    onChange={(e) => setSearchValue(e.target.value)}
                                    suffix={
                                        <>
                                            <Tooltip title="Buscar archivos por nombre" placement="top">
                                                <Button type="primary" icon={<SearchOutlined />} onClick={onClickBuscar} style={{ marginRight: 8, visibility: isCollapsed ? 'hidden' : 'visible' }} />
                                            </Tooltip>
                                            <Tooltip title="Restablecer filtros" placement="top">
                                                <Button icon={<ReloadOutlined />} onClick={onLimpiarBusqueda} style={{ visibility: isCollapsed ? 'hidden' : 'visible' }} />
                                            </Tooltip>
                                            <Tooltip title="Filtrar carpetas sin archivos" placement="top">
                                                <Button icon={<FileExclamationOutlined />} onClick={handleFilterEmptyFolders} style={{ backgroundColor: activeButton === 'emptyFolders' ? 'lightblue' : 'initial', borderColor: activeButton === 'emptyFolders' ? 'blue' : 'initial', visibility: isCollapsed ? 'hidden' : 'visible' }} />
                                            </Tooltip>
                                            <Tooltip title="Filtrar carpetas con archivos" placement="top">
                                                <Button icon={<FileZipOutlined />} onClick={handleFilterFolders} style={{ backgroundColor: activeButton === 'folders' ? 'lightblue' : 'initial', borderColor: activeButton === 'folders' ? 'blue' : 'initial', visibility: isCollapsed ? 'hidden' : 'visible' }} />
                                            </Tooltip>
                                        </>
                                    }
                                />
                            </div>
                            {!isCollapsed && (
                                <Spin spinning={loading || treeData.length === 0} tip="Cargando árbol de archivos..." size="large">
                                    <div style={{ height: '500px', maxHeight: '90%', overflowY: 'auto', border: '1px solid #d9d9d9', boxSizing: 'border-box', zIndex: 11 }}>
                                        <DirectoryTree
                                            treeData={treeDataFiltrado}
                                            expandedKeys={expandedKeys}
                                            onExpand={onExpand}
                                            onSelect={handleSelect}
                                            autoExpandParent={autoExpandParent}
                                            titleRender={titleRenderer}
                                            showLine
                                            multiple
                                            defaultExpandAll
                                        />
                                    </div>
                                </Spin>
                            )}
                        </div>
                    </Sider>

                    {/* ── CONTENIDO PRINCIPAL ─────────────────────────────────── */}
                    <Content style={{ padding: 24, overflow: 'auto', background: '#fff' }}>
                        {showWarning && (
                            <Alert message="Advertencia" description="Debe seleccionar un código de tarea para ver los medios de verificación." type="warning" showIcon className="mb-3" />
                        )}
                        {loading ? (
                            <div className="text-center"><Spin size="large" /></div>
                        ) : (
                            <div className="container-fluid" style={{ padding: 0 }}>
                                {/* Descripción de la tarea */}
                                {codigo_tarea?.codigo_tarea?.codigo_tarea && (
                                    showDescriptions ? (
                                        <div style={{ position: 'relative' }}>
                                            <Tooltip title="Cerrar">
                                                <Button
                                                    type="primary"
                                                    icon={<CloseOutlined style={{ color: '#FFF', fontSize: 12 }} />}
                                                    onClick={() => setShowDescriptions(false)}
                                                    style={{ position: 'absolute', top: -15, left: -6, zIndex: 10, padding: '5px 10px', width: 20, height: 22, backgroundColor: 'red', transform: 'rotate(90deg)' }}
                                                />
                                            </Tooltip>
                                            <Descriptions bordered column={1} style={{ padding: 0 }}>
                                                <Descriptions.Item label="Actividad">{codActividad} - {nameActividad}</Descriptions.Item>
                                                <Descriptions.Item label="Tarea">{codigoTareaDesc} - {tarea}</Descriptions.Item>
                                                <Descriptions.Item label="Indicador">{indicador}</Descriptions.Item>
                                                <Descriptions.Item label="Medio de verificación">
                                                    <div>{typeof medioVerificacion === 'string' ? medioVerificacion : JSON.stringify(medioVerificacion)}</div>
                                                </Descriptions.Item>
                                            </Descriptions>
                                        </div>
                                    ) : (
                                        <Button type="link" onClick={() => setShowDescriptions(true)} icon={<CloseOutlined rotate={45} />}>
                                            Mostrar detalles
                                        </Button>
                                    )
                                )}

                                <br />
                                <Layout>
                                    <Layout style={{ padding: 0 }}>
                                        <Content style={{ padding: 0, margin: 0, minHeight: 280 }}>
                                            {/* Archivo seleccionado en raíz */}
                                            {selectedItem && !rootNodeSelected && selectedItem.type === 'file' && (
                                                <div style={{ textAlign: 'center', padding: '20px' }}>
                                                    <WarningOutlined style={{ color: 'red', marginRight: '8px', fontSize: '2rem' }} />
                                                    <h4>Seleccione el padre o la carpeta principal</h4>
                                                </div>
                                            )}

                                            {/* Vista de bienvenida */}
                                            {rootNodeSelected && !selectedItem && (
                                                <div style={{ textAlign: 'center', padding: 0, marginTop: 0 }}>
                                                    <p className="titulo_proyecto display-4">Bienvenid@</p>
                                                    {treeData.length > 0 ? (
                                                        <Table
                                                            dataSource={treeData}
                                                            pagination={tableParams.pagination}
                                                            onChange={(p) => setTableParams((prev) => ({ ...prev, pagination: p }))}
                                                            expandable={{ defaultExpandAllRows: true }}
                                                            scroll={{ y: 340 }}
                                                            rowKey={(r) => r.key}
                                                            columns={[
                                                                {
                                                                    title: 'Resumen',
                                                                    dataIndex: 'name',
                                                                    key: 'key',
                                                                    width: 260,
                                                                    render: (_, record) => {
                                                                        const name = record.type === 'file' ? processFileName(record.viewName, 50) : record.viewName;
                                                                        return (
                                                                            <Tooltip title={processFileName(record.viewName, 200)} placement="right">
                                                                                <span>
                                                                                    {record.type === 'folder' ? <FolderOutlined /> : <FileOutlined />}
                                                                                    {` ${name}`}
                                                                                </span>
                                                                            </Tooltip>
                                                                        );
                                                                    },
                                                                },
                                                                {
                                                                    title: 'Tipo',
                                                                    key: 'extension',
                                                                    width: 100,
                                                                    align: 'center',
                                                                    render: (_, record) => {
                                                                        if (typeof record.name !== 'string') return 'Desconocido';
                                                                        if (record.type === 'folder') {
                                                                            if (typeof record.fileCount !== 'undefined') {
                                                                                const pathHasTarea = record.path?.includes('tarea_221/indicador_34/');
                                                                                const count = pathHasTarea
                                                                                    ? (record.fileCount - (record.filePhoto ?? 0) === 0 ? record.fileCount : record.fileCount - (record.filePhoto ?? 0))
                                                                                    : record.fileCount;
                                                                                return <Tooltip title="Archivos en la carpeta"><span>Folder </span><Badge count={count} overflowCount={9999} style={{ backgroundColor: '#52c41a' }} /></Tooltip>;
                                                                            }
                                                                            if (record.children?.some((c) => c.type === 'file')) {
                                                                                return <Tooltip title="Archivos en la carpeta"><span>Folder </span><Badge count={record.children.length} style={{ backgroundColor: '#52c41a' }} /></Tooltip>;
                                                                            }
                                                                            return 'Folder';
                                                                        }
                                                                        const parts = record.name.split('.');
                                                                        return parts.length > 1 ? parts.pop() : 'Archivo';
                                                                    },
                                                                },
                                                            ]}
                                                        />
                                                    ) : (
                                                        <h4 className="text-center mb-2">Seleccione alguna opción del menú para navegar por los archivos</h4>
                                                    )}
                                                </div>
                                            )}

                                            {/* Vista de carpeta seleccionada */}
                                            {selectedItem && !rootNodeSelected && selectedItem.type !== 'file' && (
                                                <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                    <Row gutter={0}>
                                                        <Col span={24}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 0 }}>

                                                                <div style={{ flex: '1 1 auto' }}>
                                                                    {isEditing ? (
                                                                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                                            <Input
                                                                                value={newName}
                                                                                onChange={handleEditNameChange}
                                                                                onBlur={handleFolderNameBlur}
                                                                                onPressEnter={handleFolderNameCommit}
                                                                                style={{ width: '85%' }}
                                                                            />
                                                                            <Button type="link" icon={<SaveOutlined />} onMouseDown={suppressBlurSave} onClick={handleFolderNameCommit} className="btn btn-primary py-0">Guardar</Button>
                                                                            <Button type="link" icon={<CloseOutlined />} onMouseDown={suppressBlurSave} onClick={cancelEditMode} className="btn btn-danger py-0">Cancelar</Button>
                                                                        </div>
                                                                    ) : (
                                                                        <>
                                                                            {formattedTypeS && formattedTypeS !== 'Desconocido'
                                                                                ? <strong>{formattedTypeS}: </strong>
                                                                                : <strong>Folder: </strong>}
                                                                            <strong>{selectedItem.viewName}</strong>
                                                                        </>
                                                                    )}
                                                                </div>
                                                                {mostrarFiltroCoincidencias && permissionAccess8 && (
                                                                    <div style={{ flex: '1 1 auto' }}>
                                                                        <Tooltip title="Filtra los soportes según si el nombre o la cédula tienen coincidencias con otros registros de la carpeta.">
                                                                            <Select
                                                                                value={filtroCoincidencias}
                                                                                onChange={setFiltroCoincidencias}
                                                                                style={{ width: 220 }}
                                                                                options={[
                                                                                    { label: 'Todos', value: 'todos' },
                                                                                    { label: 'Con coincidencias', value: 'repetidos' },
                                                                                    { label: 'Sin coincidencias', value: 'unicos' },
                                                                                ]}
                                                                            />
                                                                        </Tooltip>
                                                                    </div>
                                                                )}
                                                                {/* Botón subir único archivo */}
                                                                {selectedItem.type === 'folder' && permissionInsert && permissionAccess && selectedItem.children?.every((c) => c.type !== 'folder') && (
                                                                    <Tooltip title="Haga clic para cargar el soporte en esta carpeta">
                                                                        <Button
                                                                            onClick={handleOkFile}
                                                                            onMouseEnter={() => setHover(true)}
                                                                            onMouseLeave={() => setHover(false)}
                                                                            style={{ marginRight: 7, padding: '10px 0px 0px', border: 'none', cursor: 'pointer', transition: 'background 0.3s ease', boxShadow: 'none', backgroundColor: hover ? '#1677ff' : 'transparent' }}
                                                                        >
                                                                            <CloudUploadOutlined style={{ fontSize: hover ? 32 : 27, marginBottom: 10, color: hover ? '#fff' : '#1677ff', transition: 'color 0.3s ease, font-size 0.3s ease' }} />
                                                                        </Button>
                                                                    </Tooltip>
                                                                )}

                                                                {/* Toolbar de acciones */}
                                                                {!isEditing && (
                                                                    <div style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: '6px' }}>
                                                                        {permissionInsert7 && estadoActEnt && (
                                                                            <Tooltip title="Sistema de Reemplazos">
                                                                                <Button type="link" icon={<FileSyncOutlined style={{ color: '#fff' }} />} onClick={() => { setModalOpen(true); setSelectItemRemplazo(selectedItem.path); }} className="btn btn-primary py-0" />
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionUpdate && selectedItem.typeS !== 'medio_verificacion' && permissionAccess && (
                                                                            <Tooltip title="Editar nombre de la carpeta">
                                                                                <Button type="link" icon={<EditOutlined style={{ color: '#fff' }} />} onClick={toggleEditMode} className="btn btn-primary py-0" />
                                                                            </Tooltip>
                                                                        )}
                                                                        {informe332Visible && (
                                                                            <Tooltip title="Informe de capacitación">
                                                                                <Button type="link" danger icon={<BarChartOutlined />} className="btn btn-primary py-0" style={{ color: '#FFF' }} onClick={() => setOpenInforme332(true)} />
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionPrint && permissionAccess && (
                                                                            <Tooltip title="Crear un directorio en la ubicación actual.">
                                                                                <Popconfirm title="Crear directorio" description="¿Segur@ que quieres crear un directorio?" onConfirm={() => setIsModalVisible(true)} okText="Sí" cancelText="No" icon={<QuestionCircleOutlined style={{ color: 'red' }} />}>
                                                                                    <Button type="link" danger icon={<FolderAddFilled />} className="btn btn-primary py-0" style={{ color: '#FFF' }} />
                                                                                </Popconfirm>
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionDelete3 && selectedItem.typeS !== 'medio_verificacion' && permissionAccess && (
                                                                            <Tooltip title="Eliminar del directorio actual">
                                                                                <Popconfirm title="Eliminación de directorio" description="¿Segur@ que quieres eliminar este directorio?" onConfirm={() => handleDeleteFolder(selectedItem.path, selectedItem)} okText="Sí" cancelText="No" icon={<QuestionCircleOutlined style={{ color: 'red' }} />}>
                                                                                    <Button type="link" danger icon={<DeleteOutlined />} className="btn btn-primary py-0" style={{ color: '#FFF', background: '#ee1a0b', borderColor: '#ee1a0b' }} />
                                                                                </Popconfirm>
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionExport && permissionAccess && (
                                                                            <Tooltip title="Descarga soportes">
                                                                                <Popconfirm title="Descargar soporte" description="¿Segur@ que quieres descargar el soporte?" onConfirm={() => handleDescargar(selectedItem)} okText="Sí" cancelText="No">
                                                                                    <Button type="link" loading={isDownloading(selectedItem?.path)} icon={!isDownloading(selectedItem?.path) && <DownloadOutlined style={{ color: '#fff' }} />} className="btn btn-primary py-0" />
                                                                                </Popconfirm>
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionInsert3 && fileInsetAll && selectedItem.type === 'folder' && permissionAccess3 && selectedItem.children?.every((c) => c.type !== 'folder') && (
                                                                            <Tooltip title="Subir varios archivos">
                                                                                <Button type="link" danger icon={<FileAddOutlined />} className="btn btn-primary py-0" style={{ color: '#FFF' }} onClick={showModalAllFile} />
                                                                            </Tooltip>
                                                                        )}
                                                                        {permissionPrint3 && fileInsetAll && selectedItem.type === 'folder' && permissionAccess3 && selectedItem.children?.every((c) => c.type !== 'folder') && (
                                                                            <FileManagerModal rutaRelativa={selectedItem} onFilesDeleted={handleFilesDeletedFromModal} rutaActividad={codigo_tarea} />
                                                                        )}
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </Col>
                                                    </Row>
                                                    <Row gutter={1}>
                                                        <Col span={24}>
                                                            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                                                                {renderSelectedFiles()}
                                                            </Space>
                                                        </Col>
                                                    </Row>
                                                </Space>
                                            )}
                                        </Content>
                                    </Layout>
                                </Layout>

                                {/* ── MODAL: CREAR CARPETA ──────────────────────── */}
                                <Modal
                                    title="Crear nueva carpeta"
                                    open={isModalVisible}
                                    onOk={handleCreateFolder}
                                    onCancel={() => { setIsModalVisible(false); setNewFolderName(''); }}
                                    okText="Crear"
                                    cancelText="Cancelar"
                                >
                                    <Input ref={inputRef} placeholder="Nombre de la nueva carpeta" value={newFolderName} onChange={handleNameChange} onKeyDown={handleKeyDown} />
                                </Modal>

                                <Modal
                                    title="Editar nombre del soporte"
                                    open={renameFileModalVisible}
                                    onOk={handleRenameFileSubmit}
                                    confirmLoading={isRenamingFile}
                                    onCancel={() => setRenameFileModalVisible(false)}
                                    cancelButtonProps={{ disabled: isRenamingFile }}
                                    okText="Guardar"
                                    cancelText="Cancelar"
                                >
                                    <Input
                                        value={renameFileValue}
                                        onChange={(e) => setRenameFileValue(e.target.value)}
                                        addonAfter={renameFileExtension}
                                        onPressEnter={handleRenameFileSubmit}
                                        disabled={isRenamingFile}
                                    />
                                </Modal>

                                {/* ── MODAL: SUBIR ÚNICO ARCHIVO ───────────────── */}
                                <Modal
                                    title={<span style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', display: 'block' }}>Cargar soportes</span>}
                                    open={isModalFileVisible}
                                    onCancel={handleCancelFile}
                                    width="45vw"
                                    style={{ maxHeight: '60vh', top: 20 }}
                                    footer={null}
                                >
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
                                            {permissionInsert && selectedItem?.pathFromClient && permissionAccess && (
                                                <div>
                                                    {mostrarPrimerTexto && (
                                                        <p style={{ textAlign: 'center', marginBottom: 20, color: '#5c5a5a' }}>
                                                            Cargar el Reporte de visita de acompañamiento en formato PDF y renombrarlo: Fecha_Reporte Visita_Cédula.
                                                        </p>
                                                    )}
                                                    {mostrarSegundoTexto && (
                                                        <p style={{ textAlign: 'center', marginBottom: 20, color: '#5c5a5a' }}>
                                                            Cargar las actas de entrega con todos sus apartados diligenciados, incluyendo el de firmas y en formato PDF.
                                                        </p>
                                                    )}

                                                    {permissionAccessFechaPoa && (
                                                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                                                            <Checkbox checked={sinFechaFile} onChange={(e) => setSinFechaFile(e.target.checked)}>
                                                                Subir sin fecha de creación
                                                            </Checkbox>
                                                        </div>
                                                    )}

                                                    {controlMensaje ? (
                                                        <Form labelCol={{ span: 10 }} wrapperCol={{ span: 50 }} layout="vertical" form={form} initialValues={{ titulo: '', resourceType: null }} onFinish={handleFormSubmit}>
                                                            <Form.Item label="Nombre del soporte" name="titulo" rules={[{ required: true, message: 'Por favor, ingrese el Nombre del soporte' }, { pattern: /^[0-9]{8}_Reporte Visita_C[0-9]+$/, message: 'Formato: ddmmyyyy_Reporte Visita_C1234567' }]}>
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item label="Tipo de soporte:" name="resourceType" rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}>
                                                                <Radio.Group><Radio value="Informe">Informe</Radio></Radio.Group>
                                                            </Form.Item>
                                                            <Form.Item label="Carga de Soporte" name="soporte" rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}>
                                                                <DraggerSingle />
                                                            </Form.Item>
                                                            <Form.Item>
                                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                                                    <Button type="primary" onClick={handleFormSubmit} style={{ marginRight: 8 }} disabled={isSubmitting}>{isSubmitting ? 'Guardando...' : 'Guardar'}</Button>
                                                                    <Button type="primary" onClick={handleCancelFile} danger>Cancelar</Button>
                                                                </div>
                                                            </Form.Item>
                                                        </Form>
                                                    ) : controlMensaje34 ? (
                                                        <Form labelCol={{ span: 10 }} wrapperCol={{ span: 50 }} layout="vertical" form={form} initialValues={{ titulo: '', resourceType: null }} onFinish={handleFormSubmit}>
                                                            <Form.Item
                                                                label="Nombre del soporte"
                                                                name="titulo"
                                                                rules={[
                                                                    { required: true, message: 'Por favor, ingrese el Nombre del soporte' },
                                                                    { pattern: /^(0[1-9]|[12][0-9]|3[01])(0[1-9]|1[0-2])(19|20)[0-9]{2}_(Infraestructura|Materiales|Insumos|Insumos_infraestructura|Informe)_([CN][0-9]+|([A-Z][a-záéíóúñ]+(?: [A-Z][a-záéíóúñ]+)*))$/, message: 'Formato incorrecto para acta de entrega' },
                                                                ]}
                                                            >
                                                                <Input />
                                                            </Form.Item>
                                                            <Form.Item label="Tipo de soporte:" name="resourceType" rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}>
                                                                <Radio.Group><Radio value="Acta_entrega">Acta de entrega</Radio></Radio.Group>
                                                            </Form.Item>
                                                            <Form.Item label="Categoría:" name="categoriaUser" rules={[{ required: true, message: 'Por favor, seleccione una categoría' }]}>
                                                                <Radio.Group>
                                                                    <Radio value="Sin categorizar">Sin categorizar</Radio>
                                                                    <Radio value="Infraestructura">Infraestructura</Radio>
                                                                    <Radio value="Materiales">Materiales</Radio>
                                                                    <Radio value="Insumos">Insumos</Radio>
                                                                    <Radio value="Insumos e infraestructura">Insumos e infraestructura</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                            <Form.Item label="Carga de Soporte" name="soporte" rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}>
                                                                <DraggerSingle />
                                                            </Form.Item>
                                                            <Form.Item>
                                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                                                    <Button type="primary" onClick={handleFormSubmit} style={{ marginRight: 8 }} disabled={isSubmitting}>{isSubmitting ? 'Guardando...' : 'Guardar'}</Button>
                                                                    <Button type="primary" onClick={handleCancelFile} danger>Cancelar</Button>
                                                                </div>
                                                            </Form.Item>
                                                        </Form>
                                                    ) : nuevaRuta ? (
                                                        <Form layout="vertical" form={form} initialValues={{ titulo: '' }} onFinish={async (values) => {
                                                            if (!fileList?.length) { message.warning('Por favor, selecciona al menos un archivo.'); return; }
                                                            const titulo = values.titulo?.trim() || `Archivo_${rutaInformacionGeneral}`;
                                                            const resourceType = `Soporte ${rutaInformacionGeneral}`;
                                                            const renamedFiles = await Promise.all(
                                                                fileList.map(async (file) => {
                                                                    const ext = file.name.split('.').pop();
                                                                    const buf = await file.arrayBuffer();
                                                                    const clean = titulo.replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
                                                                    return new File([buf], `${clean}.${ext}`, { type: file.type });
                                                                })
                                                            );
                                                            for (const f of renamedFiles) {
                                                                await handleCustomRequest({ file: f, onSuccess: () => message.success(`${f.name} guardado`), onError: () => message.error(`Error al subir ${f.name}`), resourceType, categoriaUser: 'Sin categorizar' });
                                                            }
                                                            form.resetFields();
                                                            setFileList([]);
                                                        }}>
                                                            <Form.Item label="Título del archivo" name="titulo" rules={[{ required: true, message: 'Ingrese un título para el archivo' }]}>
                                                                <Input placeholder="Ej: Archivo de soporte" allowClear style={{ borderRadius: 8 }} />
                                                            </Form.Item>
                                                            <Form.Item label="Archivo(s) a subir">
                                                                <Upload.Dragger multiple={false} beforeUpload={(f) => { setFileList([f]); return false; }} onRemove={() => setFileList([])} fileList={fileList} accept=".pdf,.docx,.xlsx,.jpg,.png,.zip" style={{ borderRadius: 12, borderColor: '#91caff', backgroundColor: '#f5faff', padding: 24 }}>
                                                                    <p className="ant-upload-drag-icon" style={{ color: '#1677ff' }}><InboxOutlined style={{ fontSize: 40 }} /></p>
                                                                    <p className="ant-upload-text">Arrastra o haz clic para seleccionar el archivo</p>
                                                                    <p className="ant-upload-hint" style={{ color: '#888' }}>Solo se permite un archivo (PDF, DOCX, XLSX, JPG, PNG o ZIP)</p>
                                                                </Upload.Dragger>
                                                            </Form.Item>
                                                            <Form.Item style={{ textAlign: 'center', marginTop: 20 }}>
                                                                <Button type="primary" htmlType="submit" size="large" style={{ borderRadius: 8, padding: '0 40px', fontWeight: 500 }}>Guardar archivo(s)</Button>
                                                            </Form.Item>
                                                        </Form>
                                                    ) : (
                                                        <Form labelCol={{ span: 10 }} wrapperCol={{ span: 50 }} layout="vertical" form={form} initialValues={{ titulo: '', resourceType: null }} onFinish={handleFormSubmit}>
                                                            <Form.Item
                                                                label="Nombre del soporte"
                                                                name="titulo"
                                                                rules={instrumento ? [] : [
                                                                    { required: true, message: 'Por favor, ingrese el Nombre del soporte' },
                                                                    { pattern: /^[0-9]{8}_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*_[A-ZÁÉÍÓÚÑ][a-zA-Z0-9 _\-áéíóúÁÉÍÓÚñÑ.,]*$/, message: 'Formato: ddmmyyyy_Tipo_Ubicación. Ej: 18112023_Memoria_ElCharco' },
                                                                ]}
                                                            >
                                                                <Input value={selectedItem?.name} />
                                                            </Form.Item>
                                                            <Form.Item label="Tipo de soporte" name="resourceType" rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}>
                                                                <Radio.Group>
                                                                    <Radio value="Memoria">Memoria</Radio>
                                                                    <Radio value="Acta">Acta</Radio>
                                                                    <Radio value="PPT">PPT</Radio>
                                                                    <Radio value="Asistencia">Asistencia</Radio>
                                                                    <Radio value="Informe">Informe</Radio>
                                                                    <Radio value="Foto">Foto</Radio>
                                                                    <Radio value="Grabación">Grabación</Radio>
                                                                    <Radio value="Correo Aprobación">Correo Aprobación</Radio>
                                                                    <Radio value="Documento técnico">Documento técnico</Radio>
                                                                    <Radio value="Otro">Otro</Radio>
                                                                </Radio.Group>
                                                            </Form.Item>
                                                            <Form.Item label="Carga de Soporte" name="soporte" rules={[{ required: true, message: 'Por favor, seleccione un soporte' }]}>
                                                                <DraggerSingle />
                                                            </Form.Item>
                                                            <Form.Item>
                                                                <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                                                                    <Button type="primary" onClick={handleFormSubmit} style={{ marginRight: 8 }} disabled={isSubmitting}>{isSubmitting ? 'Guardando...' : 'Guardar'}</Button>
                                                                    <Button type="primary" onClick={handleCancelFile} danger>Cancelar</Button>
                                                                </div>
                                                            </Form.Item>
                                                        </Form>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                        {loading && (
                                            <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', backgroundColor: 'rgba(255,255,255,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                                                <div style={{ textAlign: 'center' }}>
                                                    <Spin size="large" />
                                                    <Progress percent={0} style={{ marginTop: 16 }} />
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </Modal>

                                {/* ── MODAL: SUBIR MÚLTIPLES ARCHIVOS ──────────── */}
                                <Modal
                                    title={<span style={{ fontWeight: 'bold', fontSize: '20px', textAlign: 'center', display: 'block' }}>{esInformeEDAH ? `Cargar múltiples archivos ${rutaInformacionGeneral}` : 'Cargar múltiples soportes'}</span>}
                                    open={isModalVisibleAllFile}
                                    onOk={handleFormSubmitAllFile}
                                    onCancel={handleCancelAllFile}
                                    width="45vw"
                                    footer={[
                                        <Button key="back" onClick={handleCancelAllFile} danger>Cancelar</Button>,
                                        <Button key="submit" type="primary" loading={isSubmittingAllFile} onClick={handleFormSubmitAllFile}>
                                            {isSubmittingAllFile
                                                ? `Subiendo ${uploadProgressAllFile.current}/${uploadProgressAllFile.total}...`
                                                : 'Subir archivos'}
                                        </Button>,
                                    ]}
                                >
                                    <Form form={formAllFile} layout="vertical" initialValues={{ titulo: '', resourceType: null }}>
                                        {isSubmittingAllFile && uploadProgressAllFile.total > 0 && (
                                            <div style={{ marginBottom: 16 }}>
                                                <Progress
                                                    percent={Math.round((uploadProgressAllFile.current / uploadProgressAllFile.total) * 100)}
                                                    status="active"
                                                />
                                            </div>
                                        )}
                                        {permissionAccessFechaPoa && (
                                            <Form.Item>
                                                <Checkbox checked={sinFechaMultiple} onChange={(e) => setSinFechaMultiple(e.target.checked)}>
                                                    Subir sin fecha de creación
                                                </Checkbox>
                                            </Form.Item>
                                        )}
                                        {!esInformeEDAH && (
                                            <Form.Item label="Tipo de soporte:" name="resourceType" rules={[{ required: true, message: 'Por favor, seleccione un tipo de soporte' }]}>
                                                <Radio.Group>
                                                    <Radio value="Informe">Informe</Radio>
                                                    <Radio value="Acta_entrega">Acta de entrega</Radio>
                                                    <Radio value="Memoria">Memoria</Radio>
                                                    <Radio value="Acta">Acta</Radio>
                                                    <Radio value="PPT">PPT</Radio>
                                                    <Radio value="Asistencia">Asistencia</Radio>
                                                    <Radio value="Foto">Foto</Radio>
                                                    <Radio value="Grabación">Grabación</Radio>
                                                    <Radio value="Correo Aprobación">Correo Aprobación</Radio>
                                                    <Radio value="Documento técnico">Documento técnico</Radio>
                                                    <Radio value="Otro">Otro</Radio>
                                                </Radio.Group>
                                            </Form.Item>
                                        )}
                                        <Form.Item label="Carga de Soportes" name="soportes" rules={[{ required: true, message: 'Por favor, seleccione al menos un soporte' }]}>
                                            <div className="centered-dragger">
                                                <Dragger
                                                    multiple
                                                    fileList={fileListAllFile}
                                                    beforeUpload={() => false}
                                                    onChange={(info) => {
                                                        const uniqueMap = new Map();
                                                        const filtered = info.fileList
                                                            .filter((fw) => {
                                                                const f = fw.originFileObj;
                                                                if (!f) return false;
                                                                const sig = f.name + f.size + f.type;
                                                                if (uniqueMap.has(sig)) return false;
                                                                uniqueMap.set(sig, true);
                                                                return validateFile(f);
                                                            })
                                                            .map((fw) => ({ ...fw, status: 'done' }));
                                                        setFileListAllFile(filtered);
                                                    }}
                                                    onRemove={(file) => setFileListAllFile((prev) => prev.filter((f) => f.uid !== file.uid))}
                                                >
                                                    <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                                                    <p className="ant-upload-text">Haga clic o arrastre archivos aquí</p>
                                                    <p className="ant-upload-hint">Soporte para múltiples archivos. No se permiten carpetas.</p>
                                                </Dragger>
                                            </div>
                                        </Form.Item>
                                        {fileListAllFile.length > 0 && (
                                            <div style={{ marginTop: 16 }}>
                                                <strong>Archivos seleccionados ({fileListAllFile.length}):</strong>
                                                <Button type="link" danger size="small" onClick={() => setFileListAllFile([])} style={{ marginLeft: 10 }}>Limpiar todo</Button>
                                            </div>
                                        )}
                                    </Form>
                                </Modal>

                                {/* ── MODALES EXTERNOS ──────────────────────────── */}
                                {permissionAccess7 && (
                                    <ModalSoporteReemplazo open={modalOpen} onOpenChange={setModalOpen} limpiar={onLimpiarBusqueda} remplazo_ruta={remplazo_ruta} selectItemRemplazo={selectItemRemplazo} onUploaded={onUploaded} />
                                )}
                                {informe332Visible && (
                                    <Modal open={openInforme332} onCancel={() => setOpenInforme332(false)} footer={null} width="90%" destroyOnHidden>
                                        <Informe332 />
                                    </Modal>
                                )}
                            </div>
                        )}
                    </Content>
                </Layout>
            </Drawer>
        </>
    );
};

export default FileManagerPOA;